// The admin console: a full-screen overlay (like the Studio) with everything
// an operator configures — users/orgs/access codes, sharing grants, LLM model
// tiers, the live feed, rules, unit types, streams, and map defaults.

import './admin.css';
import type { Me, RegistryDto, StoryDto } from './api';
import { del, get, patch, post, put } from './api';
import { BASEMAP_LABELS } from './basemaps';
import { buildLabel } from './build';
import { relTime, toast } from './hud';
import { channelLabel, LIVE_KINDS } from './livekinds';
import { REGION_FLASH_DEFAULTS, type RegionFlashConfig,
  setRegionFlash } from './boundaries';
import type { EventMarkerConfig } from './eventstyle';
import { normalizeConfig } from './eventstyle';
import { TYPE_ICONS as TYPE_ICON_NAMES } from './tactical';
import { markerEditorHtml, wireMarkerEditor } from './markereditor';
import { PLACE_MINZOOM } from './objects';
import { storyTree } from './story';

const esc = (v: unknown): string => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// ---------------------------------------------------------------------------

type Render = (host: HTMLElement) => void | Promise<void>;

export interface AdminHooks {
  /** Arm the cockpit's polygon draw and resolve the closed ring (or null on
   *  cancel). Provided by main.ts, which owns the map — the console hides
   *  itself while the operator draws. */
  captureArea?: (label: string) => Promise<[number, number][] | null>;
  /** False for a SUPERUSER: the console renders only the operational
   *  sections. Undefined means admin, so existing callers are unchanged. */
  isAdmin?: boolean;
}

/** The hooks the console was opened with. Module state rather than threading a
 *  parameter through nine render functions; openAdmin overwrites it each time. */
let adminHooks: AdminHooks = {};

export function openAdmin(registry: RegistryDto, hooks: AdminHooks = {}): void {
  if (document.querySelector('.admin')) return;
  adminHooks = hooks;
  const root = document.createElement('div');
  root.className = 'admin';
  root.innerHTML = `
    <header class="admin-top">
      <h2>Admin console</h2>
      <span class="adm-build" title="The build this BROWSER is running. Assets are immutable-cached, so a tab that has not re-fetched the page keeps old code while the server is current — compare this with the deployed commit before reporting a change as missing.">build ${esc(buildLabel())}</span>
      <button class="mini" data-a="close">✕ Close</button>
    </header>
    <nav class="admin-nav"></nav>
    <main class="admin-body"></main>`;
  document.body.appendChild(root);

  // Sections a SUPERUSER may open (migration 046) — the operational half.
  // The rest need admin, and the split is by blast radius: stopping the feed
  // or editing a rule is loud and reversible, while minting an access code,
  // granting sharing or repointing model spend is not. The server enforces
  // the same split; hiding a button nobody may press is courtesy, not
  // security.
  // Aircraft coverage is OPERATIONAL — loud, reversible and obvious when
  // misused — so it sits with the live feed rather than with the sections
  // that widen access or spend money. The server agrees: every
  // /api/aircraft config route is require_operator, so a superuser can
  // both see this tab and have their writes accepted.
  const OPERATOR_SECTIONS = new Set(['rules', 'live', 'aircraft', 'types',
                                     'streams', 'map', 'markers', 'flash']);
  const ALL_SECTIONS: [string, string, Render][] = [
    ['models', 'Models', renderModels],
    ['access', 'Users & access', renderAccess],
    ['sharing', 'Sharing', (h) => renderSharing(h, registry)],
    ['stories', 'Stories', renderStories],
    ['rules', 'Rules', renderRules],
    ['live', 'Live feed', renderLive],
    ['aircraft', 'Aircraft', renderAircraft],
    ['types', 'Unit types', (h) => renderTypes(h, registry)],
    ['streams', 'Streams', renderStreams],
    ['map', 'Map defaults', (h) => renderMapDefaults(h, registry)],
    // Its own tab (2026-09-01): it lived as a collapsed fold inside Map
    // defaults and the operator could not find it. Presentation setting,
    // operator-gated like the rest of this group.
    ['markers', 'Event markers', renderMarkers],
    ['flash', 'Region flash', renderRegionFlash],
  ];
  const SECTIONS = hooks.isAdmin === false
    ? ALL_SECTIONS.filter(([k]) => OPERATOR_SECTIONS.has(k))
    : ALL_SECTIONS;

  const nav = root.querySelector('.admin-nav') as HTMLElement;
  const body = root.querySelector('.admin-body') as HTMLElement;
  for (const [key, label] of SECTIONS) {
    const b = document.createElement('button');
    b.dataset.sec = key;
    b.textContent = label;
    b.addEventListener('click', () => open(key));
    nav.appendChild(b);
  }

  function open(key: string): void {
    nav.querySelectorAll('button').forEach((b) =>
      b.classList.toggle('active', b.dataset.sec === key));
    const section = SECTIONS.find(([k]) => k === key)!;
    body.innerHTML = '<p class="adm-loading">Loading…</p>';
    void section[2](body);
  }

  const close = () => {
    document.removeEventListener('keydown', onKey);
    root.remove();
  };
  const onKey = (e: KeyboardEvent) => {
    // Hidden means the operator is drawing on the map behind the console —
    // Esc there belongs to the draw flow, not to closing the console.
    if (e.key === 'Escape' && root.style.display !== 'none') close();
  };
  document.addEventListener('keydown', onKey);
  root.querySelector('[data-a="close"]')!.addEventListener('click', close);

  // SECTIONS is never empty — a principal with no sections cannot reach the
  // ⚙ button at all — but the filter makes that invisible to the compiler.
  const first = SECTIONS[0];
  if (first) open(first[0]);
}

// --- helpers ----------------------------------------------------------------

function section(title: string, hint = ''): string {
  return `<h3>${esc(title)}</h3>${hint ? `<p class="adm-hint">${hint}</p>` : ''}`;
}

/** A section that starts COLLAPSED, for pages long enough that scrolling to
 *  the control you want costs more than one click to open it. Children stay
 *  in the DOM while shut — <details> only hides them — so every querySelector
 *  and event handler wired after render works whether or not it is open. */
function fold(title: string, hint: string, inner: string, open = false): string {
  return `<details class="adm-sec"${open ? ' open' : ''}>
    <summary><b>${esc(title)}</b>${hint ? `<small>${esc(hint)}</small>` : ''}</summary>
    ${inner}
  </details>`;
}

function fail(err: unknown): void {
  toast(err instanceof Error ? err.message : 'Request failed.');
}

// --- Models -----------------------------------------------------------------

interface LlmInfo {
  configured: boolean;
  hint?: string;
  provider?: string;
  model?: string;
  webSearch?: boolean;
  tiers?: { heavy: LlmTier; light: LlmTier };
  providersWithKeys?: string[];
  allChoices?: Record<string, string[]>;
  resting?: Record<string, { secondsLeft: number; reason: string }>;
}

type LlmTier = { provider: string; model: string; thinking?: string | null;
                 fallbacks?: string[] };

// The optional tools, and where each one is actually used — an operator
// deciding whether to arm one needs to know which lane pays for it. Ids match
// backend ALL_TOOL_IDS; the submit_* tools are absent because those are the
// output contract, not an optional capability.
const TOOL_CHOICES = [
  { id: 'lookup_place', what: 'gazetteer hits + what is mapped within 15 km',
    where: 'live extraction' },
  { id: 'recent_board', what: 'the events currently on the board',
    where: 'live extraction' },
  { id: 'distance_between', what: 'km between two refs it was shown',
    where: 'incident routing, merge + link advisors' },
  { id: 'gazetteer', what: 'look a place name up',
    where: 'incident routing, merge + link advisors' },
];

async function renderModels(host: HTMLElement): Promise<void> {
  try {
    const dto = await get<{ settings: Record<string, unknown>; llm: LlmInfo }>(
      '/api/admin/settings');
    const { settings, llm } = dto;
    // A real dropdown of the known models for every provider, grouped by
    // provider, plus "Other" for typing an id the list does not carry. A
    // provider with no key on the server is listed greyed out and named as
    // such: picking it would silently resolve the tier to another provider,
    // and leaving it out read as "that provider has no models". The text input
    // stays the value the Save handler reads; the select only writes into it.
    const knownModels = Object.entries(llm.allChoices ?? {});
    const keyed = new Set(llm.providersWithKeys ?? []);
    const modelPicker = (id: string, value: unknown, blankLabel: string): string => {
      const v = String(value ?? '');
      const custom = !!v && !knownModels.some(([, list]) => list.includes(v));
      return `<span class="adm-model">
        <select id="${id}-pick">
          <option value=""${v ? '' : ' selected'}>${esc(blankLabel)}</option>
          ${knownModels.map(([prov, list]) => {
            const ok = keyed.has(prov);
            const label = ok ? prov : `${prov} — needs ${prov.toUpperCase()}_API_KEY on the server`;
            return `<optgroup label="${esc(label)}"${ok ? '' : ' disabled'}>${list.map((m) =>
              `<option value="${esc(m)}"${m === v ? ' selected' : ''}>${esc(m)}</option>`).join('')
            }</optgroup>`;
          }).join('')}
          <option value="__custom"${custom ? ' selected' : ''}>Other — type a model id…</option>
        </select>
        <input id="${id}" value="${esc(v)}" placeholder="model id, e.g. deepseek-v4-flash"${
          custom ? '' : ' hidden'} />
      </span>`;
    };
    // A model that just failed to serve is tried last for a few minutes;
    // the line says so, with the reason on hover.
    const resting = llm.resting ?? {};
    const restNote = (m: string) => resting[m]
      ? ` <em title="${esc(resting[m].reason)}">(resting ${
        Math.ceil(resting[m].secondsLeft / 60)}m)</em>` : '';
    const tierLine = (t?: LlmTier) =>
      t ? `<b>${esc(t.model)}</b> (${esc(t.provider)}${
        t.thinking ? `, thinking ${esc(t.thinking)}` : ''})${restNote(t.model)}${
        t.fallbacks?.length
          ? ` then ${t.fallbacks.map((m) => `${esc(m)}${restNote(m)}`).join(', then ')}`
          : ''}` : '—';
    const toolsOn = new Set(
      Array.isArray(settings.llmTools) ? settings.llmTools as string[] : []);
    const thinkingSelect = (id: string, value: unknown) => {
      const v = String(value ?? '');
      return `<select id="${id}">${['', 'low', 'medium', 'high', 'max'].map((o) =>
        `<option value="${o}"${o === v ? ' selected' : ''}>${
          o || 'default (off on DeepSeek)'}</option>`).join('')}</select>`;
    };
    host.innerHTML = `
      ${section('Model tiers',
        'Heavy runs the judgment work — the suggestion analyst and unit-type '
        + 'autofill, with web search where the provider supports it. Light '
        + 'runs the high-volume live-event extraction. The provider follows '
        + 'from the model id (gemini-*, deepseek-*, claude-*, gpt-*) and '
        + 'needs its API key in .env. Thinking level applies where the model '
        + 'has such a knob — Gemini maps it to a thinking budget; DeepSeek '
        + 'maps it to reasoning effort (low→low, medium/high→high, max→max), '
        + 'where the default is OFF, never the provider default. When a model '
        + 'is rate-limited, down or too slow, the call moves to the tier fallback '
        + 'model and then to every other provider with a key; a model that just '
        + 'failed is tried last for a few minutes.')}
      <div class="adm-card">
        <p class="adm-status">${llm.configured
          ? `Keys present: <b>${esc((llm.providersWithKeys ?? []).join(', '))}</b>
             · heavy resolves to ${tierLine(llm.tiers?.heavy)}
             · light resolves to ${tierLine(llm.tiers?.light)}
             ${llm.webSearch ? ' · web search available' : ''}`
          : esc(llm.hint ?? 'No provider configured.')}</p>
        <div class="adm-quickset">
          <b>One model everywhere</b>
          <label>Model ${modelPicker('adm-all', '', 'choose a model…')}</label>
          <label>Fallback ${modelPicker('adm-all-fb', '', 'none')}</label>
          <button class="mini" id="adm-all-apply" type="button">Fill every tier</button>
          <small class="adm-hint">Fills Heavy, Light, their fallbacks and the
            sequences model below; press Save to apply. Every other provider
            with a key is still tried after these.</small>
        </div>
        <label>Heavy model
          ${modelPicker('adm-heavy', settings.llmModelHeavy,
            `from .env (LLM_MODEL_HEAVY)${llm.tiers?.heavy ? ` — now ${llm.tiers.heavy.model}` : ''}`)}</label>
        <label>Heavy thinking level
          ${thinkingSelect('adm-heavy-think', settings.llmThinkingHeavy)}</label>
        <label>Heavy fallback model
          ${modelPicker('adm-heavy-fb', settings.llmFallbackHeavy,
            'none — straight on to the other providers')}</label>
        <label>Light model
          ${modelPicker('adm-light', settings.llmModelLight,
            `from .env (LLM_MODEL_LIGHT)${llm.tiers?.light ? ` — now ${llm.tiers.light.model}` : ''}`)}</label>
        <label>Light thinking level
          ${thinkingSelect('adm-light-think', settings.llmThinkingLight)}</label>
        <label>Light fallback model
          ${modelPicker('adm-light-fb', settings.llmFallbackLight,
            'none — straight on to the other providers')}</label>
        <label>Sequences model
          ${modelPicker('adm-seq-model', settings.sequencesModel, 'same as the light tier')}</label>
        <label>Incident grouping
          <select id="adm-routing">
            <option value="model"${settings.incidentRouting !== 'rules'
              ? ' selected' : ''}>model — the light tier decides, rules shadow +
              fallback</option>
            <option value="rules"${settings.incidentRouting === 'rules'
              ? ' selected' : ''}>rules — deterministic matcher only</option>
          </select></label>
        <label class="adm-check"><input type="checkbox" id="adm-economy"
          ${settings.llmEconomy !== false ? 'checked' : ''} />
          <span>Economy mode
            <small>Fewer tokens for the same feed. Extraction waits 4 min after
              an event (2 when off) and 15 min otherwise, takes one lookup round,
              and only events — not news — count as activity; incident grouping
              gathers reports for up to 5 min; the advisors look every 2 h and
              skip a picture they have already judged. While the feed is SLOW
              (under 20 posts in the last hour) it saves far more: 30-min
              batches (10 after an event) with no lookups, grouping every
              20 min, advisors every 4 h. A burst across channels or a full
              batch always goes at once. The Live page shows which applies.</small></span></label>
        <label>Base area of interest (live extraction)
          <textarea id="adm-base-area" rows="3" maxlength="500"
            placeholder="empty = scope purely by the tracked situations (stories)"
            title="The extraction prompt&#39;s standing AREA OF INTEREST prose. Active public stories always widen it with equal standing — this is the floor that applies with no story. Leave empty once your stories say it all: scope then comes entirely from them.">${
            esc(String(settings.liveBaseArea ?? ''))}</textarea></label>
        <label>Turned down (majors only)
          <textarea id="adm-quiet-area" rows="2" maxlength="500"
            placeholder="empty = no turndown"
            title="Ground the extractor still tracks but reports QUIETLY: only major events (deaths, a strike on a significant target, a leadership hit, real escalation) — routine exchanges, interceptions and shelling are held back (the ingest audit lists them as turned_down). Volume only: it never removes an area or a story from scope.">${
            esc(String(settings.liveQuietArea ?? ''))}</textarea></label>
        <label>Re-run the suggestion analyst every
          <div class="range-field">
            <input id="adm-suggest-interval" type="number" min="0.25" max="168" step="0.25"
              value="${Number(settings.suggestionIntervalHours ?? 24)}" />
            <span>hours</span></div></label>
        <fieldset class="adm-tools">
          <legend>Tools the models may call</legend>
          <p class="adm-hint">DeepSeek and Gemini. Each is independent — every call
            lands in the last-prompt transcript, and the prompt describes
            exactly the tools that are ticked.</p>
          ${TOOL_CHOICES.map((t) => `
          <label class="adm-check"><input type="checkbox" class="adm-tool"
            value="${t.id}"${toolsOn.has(t.id) ? ' checked' : ''} />
            <span><code>${t.id}</code> — ${esc(t.what)}
              <small>${esc(t.where)}</small></span></label>`).join('')}
        </fieldset>
        <label class="adm-check"><input type="checkbox" id="adm-autostart"
          ${settings.liveAutostart ? 'checked' : ''} />
          Auto-start live tracking on boot</label>
        <footer><button class="mini commit" id="adm-models-save">Save</button></footer>
      </div>`;
    host.querySelectorAll<HTMLSelectElement>('.adm-model select').forEach((sel) => {
      const input = sel.nextElementSibling as HTMLInputElement;
      sel.addEventListener('change', () => {
        if (sel.value === '__custom') {
          input.hidden = false;
          input.focus();
          return;
        }
        input.hidden = true;
        input.value = sel.value;
      });
    });
    const setPicker = (id: string, value: string): void => {
      const input = host.querySelector(`#${id}`) as HTMLInputElement;
      const sel = host.querySelector(`#${id}-pick`) as HTMLSelectElement;
      input.value = value;
      const known = [...sel.options].some((o) => o.value === value);
      sel.value = known ? value : '__custom';
      input.hidden = known;
    };
    host.querySelector('#adm-all-apply')!.addEventListener('click', () => {
      const model = (host.querySelector('#adm-all') as HTMLInputElement).value.trim();
      const fb = (host.querySelector('#adm-all-fb') as HTMLInputElement).value.trim();
      if (!model) { toast('Choose a model first.'); return; }
      for (const id of ['adm-heavy', 'adm-light', 'adm-seq-model']) setPicker(id, model);
      for (const id of ['adm-heavy-fb', 'adm-light-fb']) setPicker(id, fb);
      toast('Filled — press Save to apply.');
    });
    host.querySelector('#adm-models-save')!.addEventListener('click', async () => {
      try {
        await put('/api/admin/settings', {
          llmModelHeavy: (host.querySelector('#adm-heavy') as HTMLInputElement).value.trim(),
          llmModelLight: (host.querySelector('#adm-light') as HTMLInputElement).value.trim(),
          llmFallbackHeavy: (host.querySelector('#adm-heavy-fb') as HTMLInputElement).value.trim(),
          llmFallbackLight: (host.querySelector('#adm-light-fb') as HTMLInputElement).value.trim(),
          llmThinkingHeavy:
            (host.querySelector('#adm-heavy-think') as HTMLSelectElement).value,
          llmThinkingLight:
            (host.querySelector('#adm-light-think') as HTMLSelectElement).value,
          sequencesModel:
            (host.querySelector('#adm-seq-model') as HTMLInputElement).value.trim(),
          incidentRouting:
            (host.querySelector('#adm-routing') as HTMLSelectElement).value,
          llmEconomy: (host.querySelector('#adm-economy') as HTMLInputElement).checked,
          llmTools: [...host.querySelectorAll<HTMLInputElement>('.adm-tool')]
            .filter((b) => b.checked).map((b) => b.value),
          liveAutostart: (host.querySelector('#adm-autostart') as HTMLInputElement).checked,
          liveBaseArea:
            (host.querySelector('#adm-base-area') as HTMLTextAreaElement).value.trim(),
          liveQuietArea:
            (host.querySelector('#adm-quiet-area') as HTMLTextAreaElement).value.trim(),
          suggestionIntervalHours: Number(
            (host.querySelector('#adm-suggest-interval') as HTMLInputElement).value),
        });
        toast('Models saved — applies immediately.');
        void renderModels(host);
      } catch (err) { fail(err); }
    });
  } catch (err) {
    host.innerHTML = '<p class="adm-hint">Could not load settings.</p>';
    fail(err);
  }
}

// --- Users & access ---------------------------------------------------------

interface UserDto {
  id: string; username: string; org: string; isAdmin: boolean;
  isSuperuser?: boolean;
  disabled: boolean;
  lastLoginAt?: string | null; lastSeenAt?: string | null;
  online?: boolean; sessions?: number;
}

interface PresenceDto {
  onlineUsers: number; guestSessionsOnline: number;
  guestSessionsActive: number; windowMinutes: number;
}

// "5m ago" / "3d ago" for the presence columns; em dash when never.
function agoCell(iso: string | null | undefined): string {
  return iso ? `${relTime(iso)} ago` : '—';
}

async function renderAccess(host: HTMLElement): Promise<void> {
  try {
    const [{ users, presence }, { orgs }, { codes }, me] = await Promise.all([
      get<{ users: UserDto[]; presence?: PresenceDto }>('/api/admin/users'),
      get<{ orgs: { id: string; slug: string; name: string; userCount: number }[] }>(
        '/api/admin/orgs'),
      get<{ codes: {
        id: string; label: string; enabled: boolean;
        lastUsedAt?: string | null; lastSeenAt?: string | null;
        online?: boolean; sessions?: number;
      }[] }>('/api/admin/codes'),
      get<Me>('/api/me'),
    ]);
    const presenceLine = presence
      ? `<p class="adm-status"><span class="adm-dot on"></span>
         <b>${presence.onlineUsers}</b> user${presence.onlineUsers === 1 ? '' : 's'} online
         · <b>${presence.guestSessionsOnline}</b> guest session${
           presence.guestSessionsOnline === 1 ? '' : 's'} online
         (${presence.guestSessionsActive} unexpired) — active within the last
         ${presence.windowMinutes} min</p>`
      : '';
    host.innerHTML = `
      ${section('Users')}
      ${presenceLine}
      <table class="adm-table"><thead><tr>
        <th>Username</th><th>Org</th><th>Role</th><th>Status</th>
        <th>Last login</th><th>Last seen</th><th></th>
      </tr></thead><tbody>${users.map((u) => {
        const self = u.id === me.userId;
        return `
        <tr data-id="${u.id}">
          <td><span class="adm-dot${u.online ? ' on' : ''}" title="${
            u.online ? 'Online now' : 'Offline'}"></span>${esc(u.username)}${
            self ? ' <span class="adm-you">you</span>' : ''}</td>
          <td>${esc(u.org)}</td>
          <td>${u.isAdmin ? 'admin' : u.isSuperuser ? 'superuser' : ''}</td>
          <td>${u.disabled ? '<i class="adm-off">disabled</i>' : 'active'}</td>
          <td>${esc(agoCell(u.lastLoginAt))}</td>
          <td title="${u.sessions ?? 0} unexpired session${
            (u.sessions ?? 0) === 1 ? '' : 's'}">${esc(agoCell(u.lastSeenAt))}</td>
          <td class="adm-actions">
            ${self ? '' : `
            <button class="mini" data-u="toggle">${u.disabled ? 'Enable' : 'Disable'}</button>
            <button class="mini" data-u="admin">${u.isAdmin ? 'Revoke admin' : 'Make admin'}</button>
            <button class="mini" data-u="super" title="Live feed, rules, unit types, streams and map defaults — not users, sharing or models">${
              u.isSuperuser ? 'Revoke superuser' : 'Make superuser'}</button>`}
            <button class="mini" data-u="pw">Reset password</button>
          </td>
        </tr>`;
      }).join('')}</tbody></table>
      <div class="adm-card adm-inline-form">
        <input id="nu-name" placeholder="username" />
        <input id="nu-pass" type="password" placeholder="password (min 8)" />
        <select id="nu-org">${orgs.map((o) =>
          `<option value="${esc(o.slug)}">${esc(o.slug)}</option>`).join('')}</select>
        <label class="adm-check"><input type="checkbox" id="nu-admin" />admin</label>
        <label class="adm-check"><input type="checkbox" id="nu-super" />superuser</label>
        <button class="mini commit" id="nu-create">Add user</button>
      </div>

      ${section('Organizations')}
      <table class="adm-table"><thead><tr><th>Slug</th><th>Name</th><th>Users</th>
      </tr></thead><tbody>${orgs.map((o) => `
        <tr><td>${esc(o.slug)}</td><td>${esc(o.name)}</td><td>${o.userCount}</td></tr>`)
        .join('')}</tbody></table>
      <div class="adm-card adm-inline-form">
        <input id="no-slug" placeholder="slug (a-z, -)" />
        <input id="no-name" placeholder="Display name" />
        <button class="mini commit" id="no-create">Add org</button>
      </div>

      ${section('Access codes', 'Guest sign-in codes — the old passcode flow. '
        + 'Signing in with a code is anonymous: it never touches a user row, '
        + 'so code users’ activity shows HERE, not in the users table. For '
        + 'per-person visibility, give each person their own labelled code.')}
      <table class="adm-table"><thead><tr>
        <th>Label</th><th>Status</th><th>Last used</th><th>Last seen</th><th></th>
      </tr></thead><tbody>${codes.map((c) => `
        <tr data-id="${c.id}">
          <td><span class="adm-dot${c.online ? ' on' : ''}" title="${
            c.online ? 'A session on this code is active now' : 'No active session'
          }"></span>${esc(c.label)}</td>
          <td>${c.enabled ? 'enabled' : '<i class="adm-off">disabled</i>'}</td>
          <td>${esc(agoCell(c.lastUsedAt))}</td>
          <td title="${c.sessions ?? 0} unexpired session${
            (c.sessions ?? 0) === 1 ? '' : 's'}">${esc(agoCell(c.lastSeenAt))}</td>
          <td class="adm-actions"><button class="mini" data-c="toggle">
            ${c.enabled ? 'Disable' : 'Enable'}</button></td>
        </tr>`).join('')}</tbody></table>
      <div class="adm-card adm-inline-form">
        <input id="nc-code" placeholder="code (min 6 chars)" />
        <input id="nc-label" placeholder="label" />
        <button class="mini commit" id="nc-create">Add code</button>
      </div>`;

    host.querySelectorAll<HTMLElement>('tr[data-id] [data-u]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const row = btn.closest('tr')!;
        const user = users.find((u) => u.id === row.dataset.id)!;
        try {
          if (btn.dataset.u === 'super') {
            await put(`/api/admin/users/${user.id}`,
                      { isSuperuser: !user.isSuperuser });
          } else if (btn.dataset.u === 'toggle') {
            await put(`/api/admin/users/${user.id}`, { disabled: !user.disabled });
          } else if (btn.dataset.u === 'admin') {
            await put(`/api/admin/users/${user.id}`, { isAdmin: !user.isAdmin });
          } else {
            const pw = prompt(`New password for ${user.username} (min 8 chars):`);
            if (!pw) return;
            await put(`/api/admin/users/${user.id}`, { password: pw });
          }
          void renderAccess(host);
        } catch (err) { fail(err); }
      });
    });
    host.querySelector('#nu-create')!.addEventListener('click', async () => {
      try {
        const username = (host.querySelector('#nu-name') as HTMLInputElement).value.trim();
        await post('/api/admin/users', {
          username,
          password: (host.querySelector('#nu-pass') as HTMLInputElement).value,
          org: (host.querySelector('#nu-org') as HTMLSelectElement).value,
          isAdmin: (host.querySelector('#nu-admin') as HTMLInputElement).checked,
          isSuperuser: (host.querySelector('#nu-super') as HTMLInputElement).checked,
        });
        toast(`User '${username}' created.`);
        void renderAccess(host);
      } catch (err) { fail(err); }
    });
    host.querySelector('#no-create')!.addEventListener('click', async () => {
      try {
        await post('/api/admin/orgs', {
          slug: (host.querySelector('#no-slug') as HTMLInputElement).value.trim(),
          name: (host.querySelector('#no-name') as HTMLInputElement).value.trim(),
        });
        toast('Org created.');
        void renderAccess(host);
      } catch (err) { fail(err); }
    });
    host.querySelectorAll<HTMLElement>('tr[data-id] [data-c]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const row = btn.closest('tr')!;
        const code = codes.find((c) => c.id === row.dataset.id)!;
        try {
          await put(`/api/admin/codes/${code.id}`, { enabled: !code.enabled });
          void renderAccess(host);
        } catch (err) { fail(err); }
      });
    });
    host.querySelector('#nc-create')!.addEventListener('click', async () => {
      try {
        await post('/api/admin/codes', {
          code: (host.querySelector('#nc-code') as HTMLInputElement).value,
          label: (host.querySelector('#nc-label') as HTMLInputElement).value.trim(),
        });
        toast('Access code created.');
        void renderAccess(host);
      } catch (err) { fail(err); }
    });
  } catch (err) {
    host.innerHTML = '<p class="adm-hint">Could not load users.</p>';
    fail(err);
  }
}

// --- Sharing (policy grants) -------------------------------------------------

async function renderSharing(host: HTMLElement, registry: RegistryDto): Promise<void> {
  try {
    const { grants } = await get<{ grants: {
      id: string; subject: string; scope: string; category: string | null;
      categoryLabel: string | null; action: string; note: string | null;
    }[] }>('/api/admin/grants');
    host.innerHTML = `
      ${section('Sharing grants',
        'Who can see (or edit) whose objects, beyond the built-in "your own '
        + 'org" default. Guests only see what a grant gives them.')}
      <table class="adm-table"><thead><tr>
        <th>Who</th><th>Whose objects</th><th>Category</th><th>Action</th>
        <th>Note</th><th></th>
      </tr></thead><tbody>${grants.map((g) => `
        <tr data-id="${g.id}">
          <td>${esc(g.subject)}</td><td>${esc(g.scope)}</td>
          <td>${esc(g.categoryLabel ?? 'all')}</td><td>${esc(g.action)}</td>
          <td>${esc(g.note ?? '')}</td>
          <td class="adm-actions"><button class="mini" data-g="del">Remove</button></td>
        </tr>`).join('')
        || '<tr><td colspan="6" class="adm-hint">No grants yet.</td></tr>'}
      </tbody></table>
      <div class="adm-card adm-inline-form">
        <select id="ng-subkind">
          <option value="guest">guests</option><option value="all">everyone</option>
          <option value="org">org…</option><option value="user">user…</option>
        </select>
        <input id="ng-sub" placeholder="org slug / username" hidden />
        <span class="adm-sep">may</span>
        <select id="ng-action"><option>read</option><option>write</option></select>
        <select id="ng-cat"><option value="">all categories</option>
          ${registry.categories.map((c) =>
            `<option value="${esc(c.key)}">${esc(c.label)}</option>`).join('')}</select>
        <span class="adm-sep">of</span>
        <select id="ng-scopekind">
          <option value="all">everyone's objects</option>
          <option value="org">an org's…</option><option value="user">a user's…</option>
        </select>
        <input id="ng-scope" placeholder="org slug / username" hidden />
        <input id="ng-note" placeholder="note (optional)" />
        <button class="mini commit" id="ng-create">Grant</button>
      </div>`;

    const subKind = host.querySelector('#ng-subkind') as HTMLSelectElement;
    const subRef = host.querySelector('#ng-sub') as HTMLInputElement;
    subKind.addEventListener('change', () => {
      subRef.hidden = !['org', 'user'].includes(subKind.value);
    });
    const scopeKind = host.querySelector('#ng-scopekind') as HTMLSelectElement;
    const scopeRef = host.querySelector('#ng-scope') as HTMLInputElement;
    scopeKind.addEventListener('change', () => {
      scopeRef.hidden = !['org', 'user'].includes(scopeKind.value);
    });

    host.querySelectorAll<HTMLElement>('[data-g="del"]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await del(`/api/admin/grants/${btn.closest('tr')!.dataset.id}`);
          void renderSharing(host, registry);
        } catch (err) { fail(err); }
      });
    });
    host.querySelector('#ng-create')!.addEventListener('click', async () => {
      try {
        await post('/api/admin/grants', {
          subjectKind: subKind.value,
          subject: subRef.hidden ? null : subRef.value.trim(),
          scopeKind: scopeKind.value,
          scope: scopeRef.hidden ? null : scopeRef.value.trim(),
          category: (host.querySelector('#ng-cat') as HTMLSelectElement).value || null,
          action: (host.querySelector('#ng-action') as HTMLSelectElement).value,
          note: (host.querySelector('#ng-note') as HTMLInputElement).value,
        });
        void renderSharing(host, registry);
      } catch (err) { fail(err); }
    });
  } catch (err) {
    host.innerHTML = '<p class="adm-hint">Could not load grants.</p>';
    fail(err);
  }
}

// --- Stories -----------------------------------------------------------------
//
// Two views sharing one host: the list, and one story's detail. admin.ts's nav
// IS the router — any nav button blanks the body without warning — so a story
// is edited through several small cards that each commit on their own, and a
// successful save NEVER re-renders the view: a half-written brief in a sibling
// card would go with it. Both views fetch their own data; the registry snapshot
// openAdmin() captured says nothing about stories.

/** The story rows as the story routes send them. api.ts's StoryDto is the
 *  shared display shape; the console also reads the bookkeeping columns the map
 *  has no use for. */
interface StoryRow extends StoryDto {
  /** Slug of the owning org — set only while the story is org-scoped. */
  ownerOrg?: string | null;
  /** AOI envelope [west, south, east, north]; the list omits the geometry. */
  bbox?: [number, number, number, number] | null;
  briefRev?: number;
  createdAt?: string;
  updatedAt?: string;
}

/** Section order, labels, help and caps travel with every brief payload so this
 *  editor builds itself from the server's definition rather than a second list
 *  that drifts the day a section is added. */
interface BriefMeta {
  sectionOrder: string[];
  sectionLabels: Record<string, string>;
  sectionHelp: Record<string, string>;
  sectionCaps: Record<string, number>;
  cap: number;
}

interface BriefRev {
  rev: number;
  sections: Record<string, string>;
  rendered: string;
  authoredBy: 'human' | 'model';
  modelId: string | null;
  note: string | null;
  createdAt: string | null;
  createdBy: string | null;
  chars: number;
}

interface BriefDto extends BriefRev, BriefMeta {
  storyId: string;
  /** Ancestors composed root-first, nearest descendant winning — the bytes a
   *  model actually receives, which is not this story's `rendered` alone. */
  effective: string;
}

interface DraftDto extends BriefMeta {
  sections: Record<string, string>;
  rendered: string;
  modelId: string | null;
  provider: string | null;
  basedOnEvents: number;
}

/** Live size readout for the brief editor. Mirrors app/stories/brief.render()
 *  using the order and headings the server sent, so only the joining bytes are
 *  duplicated; the server stays the authority and 422s over the cap. */
function renderedSize(sections: Record<string, string>, meta: BriefMeta): number {
  const parts: string[] = [];
  for (const key of meta.sectionOrder) {
    const text = (sections[key] ?? '').trim();
    if (text) parts.push(`${(meta.sectionLabels[key] ?? key).toUpperCase()}\n${text}`);
  }
  return parts.length ? `STORY BRIEF\n\n${parts.join('\n\n')}`.length : 0;
}

async function renderStories(host: HTMLElement): Promise<void> {
  try {
    // status=all: archived stories are gone from the cockpit picker by design,
    // and this console is the only place left to find one again.
    const [{ stories }, defaultStory] = await Promise.all([
      get<{ stories: StoryRow[] }>('/api/stories?status=all'),
      get<{ defaultStory?: string }>('/api/config')
        .then((c) => c.defaultStory ?? '', () => ''),
    ]);
    const nameOf = new Map(stories.map((s) => [s.id, s.name] as const));
    const hasChildren = new Set(stories.map((s) => s.parentId).filter(Boolean));
    host.innerHTML = `
      ${section('Stories',
        'A story is a tracked situation — a conflict, an investigation, a '
        + 'watch. Objects are filed into one, but anything unfiled stays '
        + 'visible under every story, so creating one hides nothing. '
        + 'Org-scoped is the exception and the only narrowing axis: it hides '
        + 'the story <em>and everything filed under it</em> from every other '
        + 'org, in the lists and in the map tiles alike.')}
      <table class="adm-table"><thead><tr>
        <th>Name</th><th>Kind</th><th>Status</th><th>Visibility</th>
        <th>Filed</th><th>Brief</th><th>Updated</th><th></th>
      </tr></thead><tbody>${stories.map((s) => `
        <tr data-id="${esc(s.id)}">
          <td><b>${esc(s.name)}</b> <code>${esc(s.slug)}</code>${
            s.id === defaultStory ? ' <span class="adm-tag">default</span>' : ''}${
            !s.bbox && hasChildren.has(s.id) ? ' <span class="adm-tag">group</span>' : ''}${s.parentId
            ? `<br /><small>under ${esc(nameOf.get(s.parentId) ?? 'a story you cannot see')}</small>`
            : ''}</td>
          <td>${esc(s.kind)}</td>
          <td>${s.status === 'active' ? '<b class="adm-on">active</b>'
            : `<i class="adm-off">${esc(s.status)}</i>`}</td>
          <td>${s.visibility === 'org'
            ? `<span class="adm-tag warn">org · ${esc(s.ownerOrg ?? '?')}</span>`
            : 'public'}</td>
          <td>${s.memberCount ?? 0}</td>
          <td>rev ${s.briefRev ?? 0}</td>
          <td>${esc(agoCell(s.updatedAt))}</td>
          <td class="adm-actions"><button class="mini" data-s="edit">Edit</button></td>
        </tr>`).join('')
        || '<tr><td colspan="8" class="adm-hint">No stories yet.</td></tr>'}
      </tbody></table>
      <div class="adm-card adm-inline-form">
        <input id="nst-slug" placeholder="slug (a-z, 0-9, -)" />
        <input id="nst-name" placeholder="Display name" />
        <input id="nst-kind" placeholder="kind (conflict)" />
        <select id="nst-vis">
          <option value="public">public</option>
          <option value="org">org-scoped</option>
        </select>
        <button class="mini commit" id="nst-create">Add story</button>
      </div>
      <p class="adm-hint">A new story has no area and no members. Give it an
        area first, then Adopt files whatever is already inside it.</p>
      <p class="adm-hint"><b>Grouping stories:</b> add a story with kind
        <code>group</code> and <em>no area</em>, then set it as the Parent story
        of the ones it holds. Picking the group in the cockpit shows all of them
        together. With no area it is never offered to the live feed as a
        situation, so nothing is filed into it directly — events go to the
        stories under it. Map defaults → Default story makes a group what
        everyone opens on.</p>`;

    host.querySelectorAll<HTMLElement>('[data-s="edit"]').forEach((btn) => {
      btn.addEventListener('click', () =>
        void renderStory(host, btn.closest('tr')!.dataset.id!));
    });
    host.querySelector('#nst-create')!.addEventListener('click', async () => {
      const value = (id: string) =>
        (host.querySelector(id) as HTMLInputElement).value.trim();
      try {
        const created = await post<StoryRow>('/api/stories', {
          slug: value('#nst-slug'),
          name: value('#nst-name'),
          kind: value('#nst-kind') || 'conflict',
          visibility: (host.querySelector('#nst-vis') as HTMLSelectElement).value,
        });
        toast(`Story '${created.name}' created.`);
        void renderStory(host, created.id);
      } catch (err) { fail(err); }
    });
  } catch (err) {
    host.innerHTML = '<p class="adm-hint">Could not load stories.</p>';
    fail(err);
  }
}

async function renderStory(host: HTMLElement, id: string): Promise<void> {
  const url = (suffix = ''): string =>
    `/api/stories/${encodeURIComponent(id)}${suffix}`;
  host.innerHTML = '<p class="adm-loading">Loading…</p>';
  const loaded = await Promise.all([
    get<StoryRow>(url()),
    get<{ stories: StoryRow[] }>('/api/stories?status=all'),
    get<BriefDto>(url('/brief')),
  ]).catch((err: unknown) => { fail(err); return null; });
  if (!loaded) {
    host.innerHTML = '<p class="adm-hint">Could not load that story.</p>';
    return;
  }
  // Bindings are const; the cards mutate these objects in place from each save
  // response so the readouts stay honest without a re-render.
  const [story, { stories: list }, brief] = loaded;

  // Offering a descendant as a parent only to have the server 422 is noise; the
  // refusal itself is app/stories/service.py's, and stays there.
  const banned = new Set<string>([story.id]);
  for (;;) {
    const before = banned.size;
    for (const s of list) if (s.parentId && banned.has(s.parentId)) banned.add(s.id);
    if (banned.size === before) break;
  }

  const bbox = story.bbox ?? null;
  /** Plain text, read back off `story` after every save so the four boxes and
   *  this line can never disagree about what is actually stored. */
  const areaText = (): string => {
    if (!story.bbox) {
      return 'none yet — nothing auto-files here and Adopt has nothing to sweep.';
    }
    const parts = story.aoi && story.aoi.type === 'MultiPolygon'
      ? story.aoi.coordinates.length : 0;
    return `${story.bbox.map((n) => n.toFixed(3)).join(', ')} `
      + `(west, south, east, north)${parts > 1 ? ` · ${parts} parts` : ''}`;
  };

  const headline = (s: StoryRow): string => `
    <h3>${esc(s.name)}</h3>
    <code>${esc(s.slug)}</code>
    <span class="adm-tag">${esc(s.kind)}</span>
    <span class="adm-tag${s.status === 'active' ? '' : ' warn'}">${esc(s.status)}</span>
    ${s.visibility === 'org'
      ? `<span class="adm-tag warn">org · ${esc(s.ownerOrg ?? '?')}</span>`
      : '<span class="adm-tag">public</span>'}`;

  const briefSections = brief.sectionOrder.map((key) => `
    <label>${esc(brief.sectionLabels[key] ?? key)}
      <small class="adm-hint">${esc(brief.sectionHelp[key] ?? '')}</small>
      <textarea data-sec="${esc(key)}" rows="3">${esc(brief.sections[key] ?? '')}</textarea>
      <span class="adm-meter" data-cnt="${esc(key)}"></span></label>`).join('');

  host.innerHTML = `
    <div class="adm-actions-bar"><button class="mini" id="st-back">← All stories</button></div>
    <div class="adm-crumb" id="st-head">${headline(story)}</div>

    <div class="adm-card">
      <label>Name<input id="st-name" value="${esc(story.name)}" /></label>
      <label>Slug<input id="st-slug" value="${esc(story.slug)}" /></label>
      <label>Kind<input id="st-kind" value="${esc(story.kind)}" /></label>
      <label>Status<select id="st-status">${
        (['active', 'dormant', 'archived'] as const).map((s) =>
          `<option value="${s}"${s === story.status ? ' selected' : ''}>${s}</option>`)
          .join('')}</select></label>
      <label>Summary<textarea id="st-summary" rows="2">${esc(story.summary ?? '')}</textarea></label>
      <p class="adm-hint">Dormant keeps the story and its members on the map but
        stops it collecting new events automatically. Archived also drops it
        from the cockpit picker; members stay filed and stay visible either way.</p>
      <footer><button class="mini commit" id="st-save-id">Save</button></footer>
    </div>

    <div class="adm-card">
      <label>Parent story<select id="st-parent">
        <option value="">— none (top level) —</option>
        ${list.filter((s) => !banned.has(s.id)).map((s) =>
          `<option value="${esc(s.id)}"${s.id === story.parentId ? ' selected' : ''}>${
            esc(s.name)}</option>`).join('')}
      </select></label>
      <p class="adm-hint">Viewing a parent unions in its children's objects.
        Visibility is never inherited — a child is exactly as visible as its own
        setting says — which is why a public story cannot sit under an
        org-scoped one, in either direction.</p>
      <footer><button class="mini commit" id="st-save-parent">Save</button></footer>
    </div>

    <div class="adm-card">
      <label>Visibility<select id="st-vis">
        <option value="public"${story.visibility === 'public' ? ' selected' : ''}>public</option>
        <option value="org"${story.visibility === 'org' ? ' selected' : ''}>org-scoped</option>
      </select></label>
      <label>Owning org (slug)<input id="st-org" value="${esc(story.ownerOrg ?? '')}"
        placeholder="your own org" /></label>
      <p class="adm-hint">Org-scoped is a real access boundary, not a filter:
        the story and every object filed under it leave the lists <em>and</em>
        the tile bytes for everyone outside that org. Going back to public
        republishes all of them. Neither direction rewrites the past — a version
        that was public when it was written stays readable at that timestamp.</p>
      <footer><button class="mini commit" id="st-save-vis">Save</button></footer>
    </div>

    <div class="adm-card">
      <p class="adm-status">Area: <b id="st-area">${esc(areaText())}</b></p>
      <div class="adm-bbox">
        <label>West<input id="st-w" type="number" step="any" value="${bbox ? bbox[0] : ''}" /></label>
        <label>South<input id="st-s" type="number" step="any" value="${bbox ? bbox[1] : ''}" /></label>
        <label>East<input id="st-e" type="number" step="any" value="${bbox ? bbox[2] : ''}" /></label>
        <label>North<input id="st-n" type="number" step="any" value="${bbox ? bbox[3] : ''}" /></label>
      </div>
      <label>…or paste a GeoJSON Polygon / MultiPolygon
        <textarea id="st-aoi" rows="4"
          placeholder='{"type": "Polygon", "coordinates": [[[34, 12], …]]}'></textarea></label>
      <p class="adm-hint">The area is what Adopt sweeps and what auto-files a
        new event that has no closer match — the smallest <em>active</em> story
        containing the point wins, so nesting a small story inside a big one
        works. Draw it on the map, paste geometry, or fill the four numbers.
        All are validated on the server.</p>
      <footer>
        <button class="mini" id="st-draw-aoi">✏ Draw on map</button>
        <button class="mini" id="st-clear-aoi">Clear area</button>
        <button class="mini commit" id="st-save-aoi">Save area</button>
      </footer>
    </div>

    <div class="adm-card">
      <p class="adm-status"><b id="st-count">${story.memberCount ?? 0}</b> objects
        filed directly here — children are counted on their own rows.</p>
      <div class="adm-actions-bar">
        <button class="mini" id="st-adopt"${bbox ? '' : ' disabled'}>Adopt everything in the area</button>
      </div>
      <p class="adm-status" id="st-adopt-out" hidden></p>
      <p class="adm-hint">Adopt files every <em>unfiled</em> object whose centre
        falls inside the area. It never takes one from another story and it only
        touches the current version, so time travel still answers with what was
        true then. It is the one-time bootstrap: it writes no revisions, which is
        why it is cheap and why open browsers keep the old scope until they
        reload. Day to day, use the "Filed under" control on an object's
        selection card instead — that one bumps a revision and propagates live.</p>
    </div>

    <div class="adm-card">
      <div class="adm-sec-head"><b>Feed coverage</b></div>
      <label>Channels feeding this story (one per line)
        <textarea id="st-channels" rows="4" placeholder="loading…"></textarea></label>
      <footer><button class="mini commit" id="st-chan-save">Save channels</button></footer>
      <p class="adm-hint">Coverage and filing are separate questions. This list
        decides which channels get <em>read</em> for this story; the area above
        decides which events end up <em>filed</em> under it. Subscribing a channel
        only ever widens what the poller reads — it never narrows another story,
        and a channel subscribed to nothing at all is still read. Adding a channel
        here creates it if it is new, but enabling it is the global control on the
        Live page.</p>
    </div>

    <div class="adm-card">
      <div class="adm-sec-head"><b>Brief</b>
        <span class="adm-tag warn" id="st-dirty" hidden>unsaved</span>
        <span class="adm-meter" id="st-size"></span></div>
      <p class="adm-status" id="st-prov"></p>
      <p class="adm-hint">The brief is standing context: it rides on every
        heavy-model call about this story — the suggestion analyst and site
        identification both read it. Parents compose root-first with the nearest
        one winning, so a child only writes what differs from its parent.</p>
      ${briefSections}
      <label>Note for the log<input id="st-note" placeholder="what changed" /></label>
      <p class="adm-warn" id="st-draft" hidden></p>
      <footer>
        <button class="mini" id="st-hist">History</button>
        <button class="mini" id="st-gen">Regenerate with the model</button>
        <button class="mini commit" id="st-brief-save">Save brief</button>
      </footer>
      <div id="st-hist-out"></div>
      <details class="adm-fold"><summary>What a model actually receives
        (<span id="st-eff-len">${brief.effective.length}</span> chars, parents included)</summary>
        <pre class="adm-pre" id="st-eff"></pre>
      </details>
    </div>

    ${story.status === 'archived' ? '' : `
    <div class="adm-card">
      <div class="adm-actions-bar"><button class="mini" id="st-archive">Archive story</button></div>
      <p class="adm-hint">Archiving hides the story from the cockpit picker.
        Nothing is deleted — versions point at it — and its objects stay filed
        and exactly as visible as they are now.</p>
    </div>`}`;

  const el = <T extends HTMLElement>(sel: string): T => host.querySelector(sel) as T;
  const head = el('#st-head');
  const value = (sel: string): string =>
    (host.querySelector(sel) as HTMLInputElement | HTMLTextAreaElement).value.trim();

  /** Commit a story patch and fold the response back in WITHOUT re-rendering —
   *  the brief card below may hold unsaved text. Returns false on a rejected
   *  save so the caller keeps whatever the operator typed. */
  const saveStory = async (patch: Record<string, unknown>,
                           ok: string): Promise<boolean> => {
    try {
      const next = await put<StoryRow>(url(), patch);
      Object.assign(story, next);
      head.innerHTML = headline(story);
      toast(ok);
      return true;
    } catch (err) { fail(err); return false; }
  };

  el('#st-back').addEventListener('click', () => void renderStories(host));

  el('#st-save-id').addEventListener('click', () => void saveStory({
    name: value('#st-name'),
    slug: value('#st-slug'),
    kind: value('#st-kind') || 'conflict',
    status: el<HTMLSelectElement>('#st-status').value,
    summary: value('#st-summary') || null,
  }, 'Story saved.'));

  el('#st-save-parent').addEventListener('click', () => void saveStory(
    { parentId: el<HTMLSelectElement>('#st-parent').value || null },
    'Parent saved.'));

  const vis = el<HTMLSelectElement>('#st-vis');
  const org = el<HTMLInputElement>('#st-org');
  const syncOrg = () => { org.disabled = vis.value !== 'org'; };
  vis.addEventListener('change', syncOrg);
  syncOrg();
  el('#st-save-vis').addEventListener('click', () => {
    if (vis.value === 'org' && story.visibility !== 'org'
      && !confirm(`Scope '${story.name}' to one org? Everything filed under it `
        + 'disappears for everyone else, including from the map tiles.')) return;
    void saveStory(vis.value === 'org'
      ? { visibility: 'org', ownerOrg: org.value.trim() || null }
      : { visibility: 'public' },
    'Visibility saved.');
  });

  const paintArea = (): void => {
    el('#st-area').textContent = areaText();
    el<HTMLButtonElement>('#st-adopt').disabled = !story.bbox;
    const box = story.bbox;
    (['#st-w', '#st-s', '#st-e', '#st-n'] as const).forEach((sel, i) => {
      el<HTMLInputElement>(sel).value = box ? String(box[i]) : '';
    });
  };
  el('#st-save-aoi').addEventListener('click', async () => {
    const raw = el<HTMLTextAreaElement>('#st-aoi').value.trim();
    let saved: boolean;
    if (raw) {
      let geom: unknown;
      try { geom = JSON.parse(raw); } catch {
        toast('That is not valid JSON.');
        return;
      }
      saved = await saveStory({ aoi: geom }, 'Area saved.');
    } else {
      const nums = ['#st-w', '#st-s', '#st-e', '#st-n']
        .map((sel) => Number(el<HTMLInputElement>(sel).value));
      if (nums.some((n) => !Number.isFinite(n))) {
        toast('Give all four box edges, or paste geometry instead.');
        return;
      }
      saved = await saveStory({ bbox: nums }, 'Area saved.');
    }
    // A rejected geometry keeps the paste — the server's 422 says what to fix,
    // and re-pasting a multipolygon by hand is not a reasonable ask.
    if (!saved) return;
    el<HTMLTextAreaElement>('#st-aoi').value = '';
    paintArea();
  });
  el('#st-clear-aoi').addEventListener('click', async () => {
    if (!confirm('Remove this story\'s area? It stops auto-filing events and '
      + 'Adopt has nothing to sweep. Objects already filed stay filed.')) return;
    if (await saveStory({ aoi: null }, 'Area cleared.')) paintArea();
  });
  el('#st-draw-aoi').addEventListener('click', async () => {
    const cap = adminHooks.captureArea;
    if (!cap) {
      toast('Drawing needs the cockpit map — reopen the console from there.');
      return;
    }
    // The console is a full-screen overlay, so it steps aside while the
    // operator draws; hidden (not closed) keeps every unsaved field intact,
    // and the Esc guard in openAdmin leaves Esc to the draw flow meanwhile.
    const overlay = document.querySelector('.admin') as HTMLElement | null;
    if (overlay) overlay.style.display = 'none';
    try {
      const ring = await cap(`Area for '${story.name}'`);
      if (ring
          && await saveStory({ aoi: { type: 'Polygon', coordinates: [ring] } },
                             'Area saved from the map.')) {
        el<HTMLTextAreaElement>('#st-aoi').value = '';
        paintArea();
      }
    } finally {
      if (overlay) overlay.style.display = '';
    }
  });

  const adopt = el<HTMLButtonElement>('#st-adopt');
  adopt.addEventListener('click', async () => {
    if (!confirm(`File every unfiled object inside '${story.name}' into it? `
      + 'Objects already in another story are left alone.')) return;
    adopt.disabled = true;
    try {
      const { filed } = await post<{ filed: number }>(url('/adopt'));
      const out = el('#st-adopt-out');
      out.innerHTML = `Filed <b>${esc(filed)}</b> object${filed === 1 ? '' : 's'}.
        Adopt writes no revisions, so this browser and every other one keeps the
        old scope until it reloads.
        <button class="mini" data-a="reload">Reload now</button>`;
      out.hidden = false;
      out.querySelector('[data-a="reload"]')!
        .addEventListener('click', () => location.reload());
      const fresh = await get<StoryRow>(url());
      Object.assign(story, fresh);
      el('#st-count').textContent = String(story.memberCount ?? 0);
    } catch (err) { fail(err); }
    adopt.disabled = false;
  });

  // --- feed coverage --------------------------------------------------------
  const chanBox = el<HTMLTextAreaElement>('#st-channels');
  void (async () => {
    try {
      const { channels } = await get<{ channels: string[] }>(url('/channels'));
      chanBox.value = channels.join('\n');
      chanBox.placeholder = 'no channels yet — this story reads the shared stream';
    } catch (err) {
      chanBox.placeholder = err instanceof Error ? err.message : 'could not load';
    }
  })();
  el<HTMLButtonElement>('#st-chan-save').addEventListener('click', async () => {
    const lines = chanBox.value.split('\n').map((s) => s.trim()).filter(Boolean);
    try {
      const { channels } = await put<{ channels: string[] }>(
        url('/channels'), { channels: lines });
      chanBox.value = channels.join('\n');
      toast('Channels saved.');
    } catch (err) { fail(err); }
  });

  // --- the brief ------------------------------------------------------------
  const areas = [...host.querySelectorAll<HTMLTextAreaElement>('[data-sec]')];
  const dirtyTag = el('#st-dirty');
  const draftLine = el('#st-draft');
  const sizeOut = el('#st-size');
  // Which model drafted the text currently in the boxes, carried into the save
  // so a committed draft keeps its provenance. Cleared once it is saved or
  // replaced by hand-written text.
  let draftModel: string | null = null;

  const readSections = (): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const area of areas) {
      const text = area.value.trim();
      if (text) out[area.dataset.sec!] = text;
    }
    return out;
  };
  const paintSizes = (): void => {
    for (const area of areas) {
      const key = area.dataset.sec!;
      const cap = brief.sectionCaps[key] ?? 0;
      const n = area.value.trim().length;
      const out = host.querySelector(`[data-cnt="${key}"]`) as HTMLElement;
      out.textContent = `${n} / ${cap}`;
      // Over-cap is trimmed on save, not refused — say so rather than letting
      // the operator discover it by reading their text back short.
      out.title = n > cap ? `Over the cap; the server trims this to ${cap}.` : '';
      out.classList.toggle('adm-over', n > cap);
    }
    const total = renderedSize(readSections(), brief);
    sizeOut.textContent =
      `${total} / ${brief.cap} chars · ~${Math.round(total / 4)} tokens`;
    sizeOut.classList.toggle('adm-over', total > brief.cap);
  };
  const setDirty = (on: boolean): void => { dirtyTag.hidden = !on; };
  const paintEffective = (): void => {
    el('#st-eff').textContent = brief.effective || '(nothing written yet)';
    el('#st-eff-len').textContent = String(brief.effective.length);
  };
  const paintProv = (): void => {
    el('#st-prov').textContent = brief.rev
      ? `rev ${brief.rev} · ${brief.authoredBy === 'model' ? 'drafted by a model' : 'written'}`
        + `${brief.createdBy ? ` by ${brief.createdBy}` : ''}`
        + `${brief.createdAt ? ` ${relTime(brief.createdAt)} ago` : ''}`
        + `${brief.modelId ? ` · ${brief.modelId}` : ''}`
        + `${brief.note ? ` · ${brief.note}` : ''}`
      : 'No brief written yet.';
  };
  for (const area of areas) {
    area.addEventListener('input', () => { setDirty(true); paintSizes(); });
  }
  paintSizes();
  paintProv();
  paintEffective();

  el('#st-brief-save').addEventListener('click', async () => {
    try {
      const saved = await put<BriefDto>(url('/brief'), {
        sections: readSections(),
        note: value('#st-note') || null,
        modelId: draftModel,
      });
      Object.assign(brief, saved);
      draftModel = null;
      draftLine.hidden = true;
      el<HTMLInputElement>('#st-note').value = '';
      setDirty(false);
      paintProv();
      paintEffective();
      toast(`Brief saved as rev ${saved.rev}.`);
    } catch (err) { fail(err); }
  });

  const gen = el<HTMLButtonElement>('#st-gen');
  gen.addEventListener('click', async () => {
    if (!confirm('Draft a new brief from this story\'s events? It overwrites '
      + 'the boxes below — nothing is saved until you press Save brief.')) return;
    // A heavy-tier call with web search runs 20-60s and nothing in this app
    // dedups requests, so the button has to hold the door itself.
    gen.disabled = true;
    gen.textContent = 'Drafting…';
    try {
      const draft = await post<DraftDto>(url('/brief/generate'));
      for (const area of areas) area.value = draft.sections[area.dataset.sec!] ?? '';
      draftModel = draft.modelId;
      draftLine.textContent =
        `Draft from ${draft.modelId ?? 'the model'} over ${draft.basedOnEvents} `
        + `event${draft.basedOnEvents === 1 ? '' : 's'}. NOT SAVED — read it, fix `
        + 'it, then press Save brief. Leaving this tab discards it.';
      draftLine.hidden = false;
      setDirty(true);
      paintSizes();
    } catch (err) { fail(err); }
    gen.disabled = false;
    gen.textContent = 'Regenerate with the model';
  });

  const histOut = el('#st-hist-out');
  el('#st-hist').addEventListener('click', async () => {
    if (histOut.innerHTML) { histOut.innerHTML = ''; return; }
    try {
      const { revs } = await get<{ revs: BriefRev[] }>(url('/brief/history'));
      histOut.innerHTML = `
        <table class="adm-table"><thead><tr>
          <th>Rev</th><th>By</th><th>When</th><th>Chars</th><th>Note</th><th></th>
        </tr></thead><tbody>${revs.map((r) => `
          <tr data-rev="${r.rev}">
            <td>${r.rev}</td>
            <td>${esc(r.createdBy ?? (r.authoredBy === 'model' ? 'model' : 'system'))}</td>
            <td>${esc(agoCell(r.createdAt))}</td>
            <td>${r.chars}</td>
            <td>${esc(r.note ?? '')}</td>
            <td class="adm-actions"><button class="mini" data-h="load">Load</button></td>
          </tr>`).join('')}</tbody></table>
        <p class="adm-hint">Loading a revision only fills the boxes. Saving then
          appends a new revision — nothing is ever overwritten.</p>`;
      histOut.querySelectorAll<HTMLElement>('[data-h="load"]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const rev = revs.find((r) => r.rev === Number(btn.closest('tr')!.dataset.rev))!;
          for (const area of areas) area.value = rev.sections[area.dataset.sec!] ?? '';
          draftModel = rev.modelId;
          draftLine.textContent = `Loaded rev ${rev.rev} into the editor. `
            + 'Save appends it as a new revision.';
          draftLine.hidden = false;
          setDirty(true);
          paintSizes();
        });
      });
    } catch (err) { fail(err); }
  });

  host.querySelector('#st-archive')?.addEventListener('click', async () => {
    if (!confirm(`Archive '${story.name}'? It leaves the cockpit picker. `
      + 'Nothing is deleted and its objects stay filed and visible.')) return;
    try {
      await del(url());
      toast('Story archived.');
      void renderStories(host);
    } catch (err) { fail(err); }
  });
}

// --- Rules ------------------------------------------------------------------

interface RuleDto {
  id: string; name: string; enabled: boolean; dsl: string;
  actions: Record<string, unknown>[]; cooldownSeconds: number;
  maxCascadeDepth: number; activeFirings: number;
}

async function renderRules(host: HTMLElement): Promise<void> {
  try {
    const { rules } = await get<{ rules: RuleDto[] }>('/api/rules');
    host.innerHTML = `
      ${section('Rules',
        'Spatial triggers over the live picture. Conditions use the rule DSL '
        + '(e.g. <code>type = "truck" and near(type = "warship", 500m)</code>); '
        + 'actions run on enter.')}
      <div class="adm-actions-bar">
        <button class="mini" id="rl-run">Evaluate all now</button>
      </div>
      <table class="adm-table"><thead><tr>
        <th>On</th><th>Name</th><th>Condition</th><th>Firing</th><th></th>
      </tr></thead><tbody>${rules.map((r) => `
        <tr data-id="${r.id}">
          <td><input type="checkbox" data-r="enable" ${r.enabled ? 'checked' : ''} /></td>
          <td>${esc(r.name)}</td>
          <td><code class="adm-dsl">${esc(r.dsl)}</code></td>
          <td>${r.activeFirings || ''}</td>
          <td class="adm-actions"><button class="mini" data-r="del">Delete</button></td>
        </tr>`).join('')
        || '<tr><td colspan="5" class="adm-hint">No rules yet.</td></tr>'}
      </tbody></table>
      <div class="adm-card">
        <label>Name<input id="nr-name" placeholder="e.g. convoy near warship" /></label>
        <label>Condition (DSL)<textarea id="nr-dsl" rows="2"
          placeholder='type = "truck" and attrs.speed > 30 and near(name ~ "warship", 500m)'></textarea></label>
        <p class="adm-hint" id="nr-check"></p>
        <label>Actions (JSON list)<textarea id="nr-actions" rows="3">[
  {"action": "notify", "title": "{subject} triggered"}
]</textarea></label>
        <label class="adm-short">Cooldown (s)<input id="nr-cd" type="number"
          value="3600" min="0" /></label>
        <footer>
          <button class="mini" id="nr-validate">Validate</button>
          <button class="mini commit" id="nr-create">Create rule</button>
        </footer>
      </div>`;

    host.querySelector('#rl-run')!.addEventListener('click', async () => {
      try {
        await post('/api/rules/run');
        toast('Rules evaluated.');
        void renderRules(host);
      } catch (err) { fail(err); }
    });
    host.querySelectorAll<HTMLInputElement>('[data-r="enable"]').forEach((cb) => {
      cb.addEventListener('change', async () => {
        const rule = rules.find((r) => r.id === cb.closest('tr')!.dataset.id)!;
        try {
          await put(`/api/rules/${rule.id}`, {
            name: rule.name, dsl: rule.dsl, actions: rule.actions,
            enabled: cb.checked, cooldownSeconds: rule.cooldownSeconds,
            maxCascadeDepth: rule.maxCascadeDepth,
          });
        } catch (err) { fail(err); cb.checked = !cb.checked; }
      });
    });
    host.querySelectorAll<HTMLElement>('[data-r="del"]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (!confirm('Delete this rule?')) return;
        try {
          await del(`/api/rules/${btn.closest('tr')!.dataset.id}`);
          void renderRules(host);
        } catch (err) { fail(err); }
      });
    });
    const check = host.querySelector('#nr-check') as HTMLElement;
    host.querySelector('#nr-validate')!.addEventListener('click', async () => {
      try {
        const out = await post<{ ok: boolean; error?: string; warnings?: string[] }>(
          '/api/rules/validate',
          { dsl: (host.querySelector('#nr-dsl') as HTMLTextAreaElement).value });
        check.textContent = out.ok
          ? (out.warnings?.length ? `Parses. Warnings: ${out.warnings.join('; ')}`
                                  : 'Parses cleanly.')
          : `Error: ${out.error}`;
      } catch (err) { fail(err); }
    });
    host.querySelector('#nr-create')!.addEventListener('click', async () => {
      let actions: unknown;
      try {
        actions = JSON.parse(
          (host.querySelector('#nr-actions') as HTMLTextAreaElement).value);
      } catch {
        check.textContent = 'Actions must be valid JSON.';
        return;
      }
      try {
        await post('/api/rules', {
          name: (host.querySelector('#nr-name') as HTMLInputElement).value.trim(),
          dsl: (host.querySelector('#nr-dsl') as HTMLTextAreaElement).value,
          actions,
          cooldownSeconds: Number((host.querySelector('#nr-cd') as HTMLInputElement).value) || 0,
        });
        void renderRules(host);
      } catch (err) { fail(err); }
    });
  } catch (err) {
    host.innerHTML = '<p class="adm-hint">Could not load rules.</p>';
    fail(err);
  }
}

// --- Live feed ---------------------------------------------------------------

/** What an unfinished poll is doing. The poll counter only moves when a poll
 *  ENDS, so a model call stuck for minutes otherwise reads as "0 polls, has
 *  not polled yet" — which looks like nothing running at all. */
type Extracting = { posts: number; since: string; model?: string; lane?: string };

function busyText(s: { pollingSince?: string | null;
                       extracting?: Extracting | null }): string {
  if (s.extracting) {
    const n = s.extracting.posts;
    const on = s.extracting.model ? ` on <b>${esc(s.extracting.model)}</b>` : '';
    return ` · <b>extracting ${n} post${n === 1 ? '' : 's'}</b>${on} for ${relTime(s.extracting.since)}`;
  }
  return s.pollingSince ? ` · polling for ${relTime(s.pollingSince)}` : '';
}

type ChainLink = { model: string; provider: string;
                   resting?: { secondsLeft: number; reason: string } | null };

/** The tier's chain as one line: the order a call walks, resting links
 *  marked with why. */
function chainText(links: ChainLink[] | undefined): string {
  if (!links?.length) return '<i class="adm-off">no model with a key</i>';
  return links.map((l) => `<b>${esc(l.model)}</b>${l.resting
    ? ` <em title="${esc(l.resting.reason)}">(resting ${Math.ceil(l.resting.secondsLeft / 60)}m)</em>`
    : ''}`).join(' → ');
}

type Economy = { enabled: boolean; regime: 'full' | 'economy' | 'slow';
                 postsPerHour: number | null };

/** Which economy regime the model lanes are spending under, and why. */
function economyText(e: Economy | undefined): string {
  if (!e) return '';
  const rate = e.postsPerHour == null ? '' : `, ${Math.round(e.postsPerHour)} posts/h`;
  if (e.regime === 'full') return ' · economy off';
  if (e.regime === 'slow') {
    return ` · <b title="Under 20 posts in the last hour: 30-min batches (10 after an event), no lookups, grouping every 20 min, advisors every 4 h. A burst or a full batch still goes at once.">economy — slow feed${rate}</b>`;
  }
  return ` · <span title="4-min batches after an event, 15 otherwise, one lookup round, grouping every 5 min, advisors every 2 h.">economy${rate}</span>`;
}

/** Why the buffer is still a buffer, in words. */
function bufferText(s: { pending?: number; flushIn?: number | null; hot?: boolean }): string {
  if (!s.pending) return '';
  const when = s.flushIn == null ? '' : s.flushIn <= 0 ? ', sending on the next poll'
    : `, sending in ~${Math.max(1, Math.round(s.flushIn / 60000))}m${s.hot ? ' (board hot)' : ''}`;
  return ` · ${s.pending} buffered${when}`;
}

async function renderLive(host: HTMLElement): Promise<void> {
  let state: {
    on: boolean; polls: number; lastPollAt: string | null; since?: string | null;
    lastError: string | null; eventCount: number; channels: string[];
    pollingSince?: string | null; extracting?: Extracting | null;
    pending?: number; flushIn?: number | null; hot?: boolean; pollMs?: number;
    models?: { light: ChainLink[]; heavy: ChainLink[] };
    economy?: Economy;
  } | null = null;
  try {
    state = await get('/api/live/state');
  } catch { /* 503 = not configured */ }
  host.innerHTML = `
    ${section('Live event tracking',
      'Polls the Telegram channels, extracts located events with the light '
      + 'model, and drops them on the map.')}
    ${state === null
      ? '<p class="adm-hint">Live tracking is not configured (no LLM key).</p>'
      : `
    <div class="adm-card">
      <p class="adm-status">
        ${state.on ? '<b class="adm-on">running</b>' : '<i class="adm-off">stopped</i>'}
        ${state.on && state.pollMs ? ` · polls every ${Math.round(state.pollMs / 1000)} s` : ''}
        ${state.lastPollAt ? ` · last poll ${relTime(state.lastPollAt)} ago` : ''}
        · ${state.polls} polls · ${state.eventCount} events${busyText(state)}${bufferText(state)}${economyText(state.economy)}
        ${state.on && state.since ? ` · posts since ${new Date(state.since).toLocaleString()}` : ''}
        ${state.lastError ? ` · <i class="adm-off">last error: ${esc(state.lastError)}</i>` : ''}
      </p>
      <p class="adm-status" title="The order a call walks: the tier's model, its fallback, then every other provider with a key. A model that just failed to serve is tried last for a few minutes (longer each time it keeps failing).">
        Light (extraction): ${chainText(state.models?.light)}<br>
        Heavy (judgment): ${chainText(state.models?.heavy)}
        · <a href="#" id="lv-models">Configure models ›</a>
      </p>
      <div class="adm-actions-bar">
        <button class="mini commit" id="lv-resume"
          title="Begin tracking and catch up from the last post already processed (up to 6 h back), so a Stop, a deploy or a rebuild does not blackhole the gap.">Start</button>
        <button class="mini" id="lv-start"
          title="Begin tracking from NOW only. Everything already on the channel pages is older than this instant and is skipped as 'published before tracking started'.">Start from now</button>
        <button class="mini" id="lv-stop">Stop</button>
        <button class="mini" id="lv-clear">Clear events</button>
      </div>
      <div class="adm-actions-bar" title="Deletes the window's public live events and its ingest-audit rows, then re-fetches that many hours of channel history (t.me pagination) and replays it through the extractor — the light model, same as live ingestion. The recovery action for a stretch the pipeline mishandled (an outage, a quota gap, a wedged batch).">
        <select id="lv-rb-hours" class="mini">
          ${[1, 2, 4, 8, 12, 16, 24, 36, 48].map((h) =>
            `<option value="${h}"${h === 1 ? ' selected' : ''}>last ${h}h</option>`).join('')}
        </select>
        <button class="mini" id="lv-rebuild">Rebuild window</button>
      </div>
    </div>`}
    ${section('Ingest audit',
      'The fate of every post the poller handled recently — sent to the model '
      + 'and kept (created / folded / merged), read and declined, discarded '
      + 'with the reason, or deferred by the per-tick cap. This is how to tell '
      + '"it was missed" from "it was seen and deliberately not kept" from '
      + '"the pipeline is not running".')}
    <div class="adm-card wide" id="lv-audit"><p class="adm-hint">Loading…</p></div>
    ${section('Model calls',
      'Every call to a model in the last day, newest first — which model was '
      + 'asked, by which lane, how many posts, tokens in and out, how long it '
      + 'took, and whether it answered. The audit above says what was DECIDED '
      + 'about each post; a row here opens the exact bytes that were SENT and '
      + 'what came back. Kept 48 h.')}
    <div class="adm-card wide" id="lv-calls"><p class="adm-hint">Loading…</p></div>
    ${section('Channel coverage',
      'Which Telegram channels the poller reads. A channel with no story '
      + 'subscription is read anyway and feeds the shared stream; subscribing it '
      + 'to a story only ever WIDENS what gets polled. Which story an event ends '
      + 'up filed under is decided by the story’s area, not by this list.')}
    <div class="adm-card wide" id="lv-cov">
      <p class="adm-hint">Loading channels…</p>
    </div>
    ${section('Analyst pass',
      'Clusters recent events into suggestions (heavy model + web search).')}
    <div class="adm-actions-bar">
      <button class="mini" id="lv-suggest">Run analyst pass now</button>
    </div>`;

  host.querySelector('#lv-suggest')!.addEventListener('click', async () => {
    try {
      const out = await post<{ created: unknown[] }>('/api/suggestions/run');
      toast(`Analyst pass done — ${out.created.length} new suggestion(s).`);
    } catch (err) { fail(err); }
  });

  // Coverage renders independently of the engine: channels are configuration,
  // and an operator must be able to set them up before an LLM key exists.
  await renderCoverage(host.querySelector('#lv-cov') as HTMLElement);
  // So does the audit — with no engine it still answers "nothing has run",
  // which is the whole point of asking.
  await renderAudit(host.querySelector('#lv-audit') as HTMLElement);
  // And so does the call log, for the same reason twice over: with no engine
  // it answers "nothing has been sent", which is the state an operator
  // staring at an empty feed is trying to distinguish.
  await renderCalls(host.querySelector('#lv-calls') as HTMLElement);

  if (state === null) return;
  host.querySelector('#lv-models')!.addEventListener('click', (ev) => {
    ev.preventDefault();
    (host.closest('.admin') ?? document)
      .querySelector<HTMLButtonElement>('button[data-sec="models"]')?.click();
  });
  host.querySelector('#lv-start')!.addEventListener('click', async () => {
    try {
      await post('/api/live/start', { minutes: 0, resume: false });
      toast('Started from now — older posts on the channel pages are skipped.');
      void renderLive(host);
    } catch (err) { fail(err); }
  });
  host.querySelector('#lv-resume')!.addEventListener('click', async () => {
    try {
      await post('/api/live/start', { minutes: 0, resume: true });
      toast('Started — catching up from the last processed post (max 6 h).');
      void renderLive(host);
    } catch (err) { fail(err); }
  });
  host.querySelector('#lv-stop')!.addEventListener('click', async () => {
    try { await post('/api/live/stop'); void renderLive(host); }
    catch (err) { fail(err); }
  });
  host.querySelector('#lv-clear')!.addEventListener('click', async () => {
    if (!confirm('Clear ALL live events from the map?')) return;
    try { await post('/api/live/clear'); void renderLive(host); }
    catch (err) { fail(err); }
  });
  host.querySelector('#lv-rebuild')!.addEventListener('click', async () => {
    const sel = host.querySelector('#lv-rb-hours') as HTMLSelectElement;
    const hours = Number(sel.value);
    if (!confirm(`Rebuild the last ${hours} hours?\n\nPublic live events in that `
      + 'window are DELETED, along with their ingest-audit rows, then that '
      + 'stretch of channel history is re-fetched '
      + 'and replayed through the extractor (light model). Runs in the '
      + 'background for a few minutes; the engine restarts itself when done.')) {
      return;
    }
    try {
      await post('/api/live/rebuild', { hours });
      toast(`Rebuild of the last ${hours}h started — progress is in the app `
        + 'log, and events reappear in the feed as batches complete.');
      void renderLive(host);
    } catch (err) { fail(err); }
  });
}

interface AuditRow {
  at: string; postId: string | null; channel: string | null;
  postedAt: string | null; disposition: string; detail: string | null;
  eventId: string | null; text: string | null;
}

interface AuditDto {
  hours: number;
  engine: { on: boolean; lastPollAt: string | null; polls: number;
            postsScanned: number; lastError: string | null;
            pending?: number; flushIn?: number | null; hot?: boolean;
            pollingSince?: string | null;
            extracting?: { posts: number; since: string } | null } | null;
  counts: Record<string, number>;
  rows: AuditRow[];
}

/** kept / declined / waiting — the three colours the disposition chips take.
 *  "folded" and "merged" count as kept: the report reached the map inside
 *  another event's card, which is the design, not a loss. "denial" too — the
 *  post disputed an event (or found none to dispute); either way it was
 *  understood and acted on, not dropped. "coverage" the same: late footage
 *  attached to the event it documents (or found none to attach to), and
 *  "detail" likewise for an outcome/casualty claim about a reported event. */
const AUDIT_KEPT = new Set(['created', 'folded', 'merged', 'denial', 'coverage',
                            'detail']);
const AUDIT_WAIT = new Set(['deferred']);

/** Above this many characters (or more than 3 lines) a post renders folded.
 *  Roughly a paragraph — enough that a normal report shows whole, small
 *  enough that a digest does not push every other row off the screen. */
const AUDIT_FOLD_CHARS = 320;

/** What each fate means, as the chip's tooltip — the table has to explain
 *  itself, because "no_event" read alone does not say whether the post was
 *  grouped, dropped or deliberately declined. The detail line under the post
 *  carries the specifics (for no_event, the model's own decline reason when
 *  it gave one); this is the standing definition of the word above it. */
const AUDIT_GLOSS: Record<string, string> = {
  created: 'A new event was created from this post.',
  folded: "Grouped in-batch into another post's event — the model read them as the same report.",
  merged: 'Repeat report of an event already on the board — recorded as a new version of it.',
  no_event: 'Sent to the model, which declined it: no reportable current event. The line under the post says why, when the model gave a reason.',
  skipped: 'Never sent to the model — no readable post date, or published before tracking started.',
  deferred: 'Still unprocessed — held back by a halved batch or the per-tick cap, and retried from the buffer.',
  dropped: 'An event was extracted, but it falls outside every area of interest.',
  unlocatable: 'An event was extracted, but no defensible coordinate could be found for it.',
  turned_down: 'A routine report from ground listed under "Turned down (majors only)" in Models — held back, never put on the map.',
  denial: 'The post disputes a reported event rather than reporting one.',
  coverage: 'Late footage or a fuller account, attached to the event it documents.',
  detail: 'An outcome or aftermath claim, attached to the event it is about.',
};

/** The last hours of per-post fates next to the engine's own state — the
 *  troubleshooting view for "was that missed, or is it just not running". */
async function renderAudit(host: HTMLElement, hours = 1): Promise<void> {
  let dto: AuditDto;
  try {
    dto = await get<AuditDto>(`/api/live/audit?hours=${hours}`);
  } catch (err) {
    host.innerHTML = `<p class="adm-hint">Could not load the audit: ${
      esc(err instanceof Error ? err.message : String(err))}</p>`;
    return;
  }

  const eng = dto.engine;
  const engLine = eng === null
    ? '<i class="adm-off">engine not constructed — no LLM key, nothing polls</i>'
    : `${eng.on ? '<b class="adm-on">running</b>' : '<i class="adm-off">stopped</i>'}`
      + (eng.lastPollAt ? ` · last poll ${relTime(eng.lastPollAt)} ago`
        : eng.pollingSince ? '' : ' · has not polled yet')
      + busyText(eng)
      + ` · ${eng.polls} polls, ${eng.postsScanned} posts scanned since start`
      // The buffer, and WHY it is still a buffer: "3 pending" alone reads
      // as stuck. "hot" is the short ceiling after the model last returned
      // something — follow-ups to a live situation go within two minutes.
      + (eng.pending
        ? ` · <b>${eng.pending} buffered</b>, extracting in ${
            eng.flushIn === null || eng.flushIn === undefined ? '—'
              : eng.flushIn <= 0 ? 'the next poll'
              : `≤${Math.ceil(eng.flushIn / 1000)} s`}${
            eng.hot ? ' (board hot)' : ''}`
        : eng.hot ? ' · board hot' : '')
      + (eng.lastError
        ? ` · <i class="adm-off">last error: ${esc(eng.lastError)}</i>` : '');
  const kept = Object.entries(dto.counts)
    .filter(([k]) => AUDIT_KEPT.has(k)).reduce((a, [, n]) => a + n, 0);
  const total = Object.values(dto.counts).reduce((a, n) => a + n, 0);
  const countsLine = Object.entries(dto.counts).sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${esc(k)} <b>${n}</b>`).join(' · ');

  const chip = (d: string): string => {
    const cls = AUDIT_KEPT.has(d) ? 'adm-on' : AUDIT_WAIT.has(d) ? '' : 'adm-off';
    return `<b class="${cls}" title="${esc(AUDIT_GLOSS[d] ?? '')}">${esc(d)}</b>`;
  };

  /** The post, WHOLE — line breaks and all. It used to render 110 characters
   *  with the rest in a title tooltip, which is unreadable for exactly the
   *  posts this table exists for: "why was this ignored" is a question about
   *  the wording, and the deciding phrase is rarely in the first line. Long
   *  posts fold behind a native <details> so one 3000-character digest cannot
   *  bury the rows under it; the summary keeps the first line as a heading, so
   *  the control reads the same open or shut and always toggles back. */
  const postCell = (text: string): string => {
    const full = `<div class="adm-audit-full">${esc(text)}</div>`;
    const lines = text.split('\n');
    if (text.length <= AUDIT_FOLD_CHARS && lines.length <= 3) return full;
    const head = lines.find((l) => l.trim()) ?? '';
    return `<details class="adm-audit-fold">
      <summary><span class="adm-audit-head">${esc(head.slice(0, 90))}${
        head.length > 90 ? '…' : ''}</span> <small class="adm-hint">${
        text.length} chars</small></summary>${full}</details>`;
  };

  const rows = dto.rows.map((r) => `
    <tr>
      <td title="${esc(r.postedAt ? `posted ${r.postedAt}` : '')}">${
        esc(relTime(r.at))} ago</td>
      <td>${r.channel ? esc(channelLabel(r.channel)) : ''}</td>
      <td>${chip(r.disposition)}</td>
      <td class="adm-audit-text">${postCell(r.text ?? '')}
        ${r.detail ? `<div class="adm-hint">${esc(r.detail)}</div>` : ''}</td>
    </tr>`).join('');

  host.innerHTML = `
    <p class="adm-status">${engLine}</p>
    <p class="adm-status">Last ${dto.hours}h: ${total
      ? `<b>${total}</b> post${total === 1 ? '' : 's'} handled, <b>${kept}</b> kept — ${countsLine}`
      : eng?.on
        ? 'no posts handled — the channels have been quiet (or the poll is failing; check Health below)'
        : 'no posts handled'}</p>
    <div class="adm-actions-bar">
      <select id="lv-audit-hours" class="mini">
        ${[1, 3, 6, 12, 24, 48].map((h) =>
          `<option value="${h}"${h === hours ? ' selected' : ''}>last ${h}h</option>`).join('')}
      </select>
      <button class="mini" id="lv-audit-refresh">Refresh</button>
    </div>
    ${dto.rows.length ? `<div class="adm-audit-scroll"><table class="adm-table">
      <thead><tr><th>When</th><th>Channel</th><th>Fate</th><th>Post</th></tr></thead>
      <tbody>${rows}</tbody></table></div>` : ''}`;

  const again = () => void renderAudit(host,
    Number((host.querySelector('#lv-audit-hours') as HTMLSelectElement).value));
  host.querySelector('#lv-audit-refresh')!.addEventListener('click', again);
  host.querySelector('#lv-audit-hours')!.addEventListener('change', again);
}

interface CallRow {
  key: string; at: number; group: string; round: number; lane: string;
  posts: number | null; provider: string; model: string; status: string;
  httpStatus: number | null; durationMs: number | null; error: string | null;
  ok: boolean | null; prompt: number; cached: number; completion: number;
  reasoning: number; inFlight: boolean; requestChars: number; responseChars: number;
  request?: string | null; response?: string | null;
}

/** One foldable block of prompt bytes with a Copy button — the bytes you
 *  paste into a provider playground to reproduce a bad extraction, and
 *  hand-selecting 30 KB in a scroll box does not work. */
function promptBlock(id: string, label: string, text: string, open = false): string {
  return `<details class="adm-prompt"${open ? ' open' : ''}>
    <summary><b>${esc(label)}</b>
      <small class="adm-hint">${text.length.toLocaleString()} chars</small>
      <button class="mini" data-copy="${esc(id)}">Copy</button></summary>
    <div class="adm-prompt-body adm-audit-full" id="${esc(id)}">${esc(text)}</div>
  </details>`;
}

function wireCopy(host: HTMLElement): void {
  // The button lives inside <summary>, so its click would otherwise toggle
  // the fold — copying and collapsing the thing you just copied.
  host.querySelectorAll<HTMLElement>('[data-copy]').forEach((b) => {
    b.addEventListener('click', async (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const src = host.querySelector(`#${b.dataset.copy!}`);
      try {
        await navigator.clipboard.writeText(src?.textContent ?? '');
        toast('Copied.');
      } catch { toast('Could not copy — the browser refused clipboard access.'); }
    });
  });
}

const fmtK = (n: number): string => n >= 10000 ? `${(n / 1000).toFixed(1)}k` : n.toLocaleString();
const fmtSecs = (ms: number | null): string =>
  ms == null ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)} s`;

/** Pull the readable parts out of a stored request: the system prompt, the
 *  first user message, and everything after them (a tool loop's rounds) —
 *  whichever of the three provider shapes it is. */
function splitRequest(raw: string): { system: string; user: string; rest: string; tools: string[] } {
  const out = { system: '', user: '', rest: '', tools: [] as string[] };
  let body: Record<string, unknown>;
  try { body = JSON.parse(raw) as Record<string, unknown>; } catch { out.user = raw; return out; }
  if (Array.isArray(body.tools)) out.tools = (body.tools as unknown[]).map(String);
  const text = (v: unknown): string => typeof v === 'string' ? v
    : Array.isArray(v) ? v.map((p) => (p as { text?: string }).text ?? '').join('') : '';
  const restOf = (items: unknown[]): string =>
    items.length ? JSON.stringify(items, null, 1) : '';
  if (Array.isArray(body.messages)) {             // OpenAI / DeepSeek / Anthropic
    const msgs = body.messages as { role: string; content?: unknown }[];
    out.system = typeof body.system === 'string' ? body.system
      : text(msgs.find((m) => m.role === 'system')?.content);
    const firstUser = msgs.findIndex((m) => m.role === 'user');
    out.user = text(msgs[firstUser]?.content);
    out.rest = restOf(msgs.filter((m, i) => m.role !== 'system' && i !== firstUser));
  } else if (Array.isArray(body.contents)) {      // Gemini
    const sys = body.systemInstruction as { parts?: { text?: string }[] } | undefined;
    out.system = (sys?.parts ?? []).map((p) => p.text ?? '').join('');
    const contents = body.contents as { role: string; parts?: unknown[] }[];
    out.user = text(contents[0]?.parts);
    out.rest = restOf(contents.slice(1));
  } else {
    out.user = raw;
  }
  return out;
}

/** The answer inside a stored response body — the text or the tool call's
 *  arguments — or the raw body when the shape is not one we know. */
function answerOf(raw: string | null | undefined): string {
  if (!raw) return '';
  let body: Record<string, unknown>;
  try { body = JSON.parse(raw) as Record<string, unknown>; } catch { return raw; }
  const pretty = (v: unknown): string => {
    if (typeof v !== 'string') return JSON.stringify(v, null, 1);
    try { return JSON.stringify(JSON.parse(v), null, 1); } catch { return v; }
  };
  const choice = (body.choices as { message?: Record<string, unknown> }[] | undefined)?.[0];
  if (choice?.message) {
    const m = choice.message;
    const calls = (m.tool_calls as { function?: { name?: string; arguments?: string } }[] | undefined) ?? [];
    return [m.reasoning_content ? `[reasoning]\n${String(m.reasoning_content)}\n` : '',
      m.content ? String(m.content) : '',
      ...calls.map((c) => `[${c.function?.name}]\n${pretty(c.function?.arguments ?? '')}`)]
      .filter(Boolean).join('\n\n');
  }
  const cand = (body.candidates as { content?: { parts?: Record<string, unknown>[] } }[] | undefined)?.[0];
  if (cand?.content?.parts) {
    return cand.content.parts.map((p) => p.functionCall
      ? `[${(p.functionCall as { name?: string }).name}]\n${pretty((p.functionCall as { args?: unknown }).args)}`
      : String(p.text ?? '')).filter(Boolean).join('\n\n');
  }
  if (Array.isArray(body.content)) {
    return (body.content as { text?: string }[]).map((c) => c.text ?? '').join('');
  }
  return raw;
}

/** The last day of model calls, newest first, with in-flight ones on top;
 *  a row opens the exact bytes sent and the answer. */
async function renderCalls(host: HTMLElement, hours = 24, openKey: string | null = null): Promise<void> {
  let dto: { hours: number; calls: CallRow[] };
  try {
    dto = await get(`/api/live/calls?hours=${hours}`);
  } catch (err) {
    host.innerHTML = `<p class="adm-hint">Could not load the call log: ${
      esc(err instanceof Error ? err.message : String(err))}</p>`;
    return;
  }
  const rows = dto.calls;
  const statusChip = (c: CallRow): string => {
    if (c.inFlight) return '<b class="adm-on">in flight</b>';
    if (c.status === 'ok' && c.ok !== false) return '<span class="adm-on">ok</span>';
    const label = c.status === 'ok' ? 'unusable' : c.status;
    return `<b class="adm-off" title="${esc(c.error ?? '')}">${esc(label)}</b>`;
  };
  const bar = `<div class="adm-actions-bar">
      <select id="lv-calls-hours" class="mini">
        ${[1, 6, 24, 48].map((h) => `<option value="${h}"${h === hours ? ' selected' : ''}>last ${h}h</option>`).join('')}
      </select>
      <button class="mini" id="lv-calls-refresh">Refresh</button>
    </div>`;
  const tally = rows.filter((c) => !c.inFlight);
  const summary = tally.length
    ? `${tally.length} calls · ${fmtK(tally.reduce((n, c) => n + c.prompt, 0))} in / ${
      fmtK(tally.reduce((n, c) => n + c.completion, 0))} out tokens · ${
      tally.filter((c) => c.status !== 'ok' || c.ok === false).length} failed`
    : 'No calls in this window.';
  host.innerHTML = `
    <p class="adm-status">${esc(summary)}</p>
    ${rows.length ? `<div class="adm-audit-scroll"><table class="adm-table adm-calls">
      <thead><tr><th>When</th><th>Lane</th><th>Model</th><th>Posts</th>
        <th title="prompt tokens (of which cached)">In</th>
        <th title="completion tokens (of which reasoning)">Out</th>
        <th>Time</th><th>Result</th></tr></thead>
      <tbody>${rows.map((c) => `
        <tr data-key="${esc(c.key)}" class="${c.key === openKey ? 'adm-open' : ''}${c.inFlight ? ' adm-flight' : ''}">
          <td title="${new Date(c.at * 1000).toLocaleString()}">${relTime(new Date(c.at * 1000).toISOString())} ago</td>
          <td>${esc(c.lane)}${c.round > 1 ? ` <small>round ${c.round}</small>` : ''}</td>
          <td>${esc(c.model || c.provider)}</td>
          <td>${c.posts ?? ''}</td>
          <td>${c.prompt ? fmtK(c.prompt) : ''}${c.cached ? ` <small>(${fmtK(c.cached)})</small>` : ''}</td>
          <td>${c.completion ? fmtK(c.completion) : ''}${c.reasoning ? ` <small>(${fmtK(c.reasoning)})</small>` : ''}</td>
          <td>${fmtSecs(c.durationMs)}${c.inFlight ? '…' : ''}</td>
          <td>${statusChip(c)}</td>
        </tr>`).join('')}</tbody>
    </table></div>` : ''}
    <div id="lv-call-detail"></div>
    ${bar}`;
  const again = (key: string | null) => void renderCalls(host,
    Number((host.querySelector('#lv-calls-hours') as HTMLSelectElement).value), key);
  host.querySelector('#lv-calls-refresh')!.addEventListener('click', () => again(openKey));
  host.querySelector('#lv-calls-hours')!.addEventListener('change', () => again(openKey));
  host.querySelectorAll<HTMLTableRowElement>('tr[data-key]').forEach((tr) => {
    tr.addEventListener('click', () => void openCall(host, tr.dataset.key!));
  });
  if (openKey) await openCall(host, openKey);
}

async function openCall(host: HTMLElement, key: string): Promise<void> {
  const pane = host.querySelector('#lv-call-detail') as HTMLElement;
  host.querySelectorAll('tr[data-key]').forEach((tr) =>
    tr.classList.toggle('adm-open', (tr as HTMLElement).dataset.key === key));
  pane.innerHTML = '<p class="adm-hint">Loading…</p>';
  let c: CallRow;
  try {
    c = await get<CallRow>(`/api/live/calls/${encodeURIComponent(key)}`);
  } catch (err) {
    pane.innerHTML = `<p class="adm-hint">Could not load: ${esc(err instanceof Error ? err.message : String(err))}</p>`;
    return;
  }
  const req = splitRequest(c.request ?? '');
  const meta = [
    `${new Date(c.at * 1000).toLocaleString()}`,
    `${c.lane}${c.round > 1 ? ` round ${c.round}` : ''}`,
    `${c.provider}/${c.model}`,
    c.posts != null ? `${c.posts} post${c.posts === 1 ? '' : 's'}` : '',
    req.tools.length ? `tools: ${req.tools.join(', ')}` : '',
    `${fmtK(c.prompt)} in (${fmtK(c.cached)} cached) · ${fmtK(c.completion)} out (${fmtK(c.reasoning)} reasoning)`,
    c.inFlight ? `in flight for ${fmtSecs(c.durationMs)}` : `${fmtSecs(c.durationMs)}`,
    c.httpStatus ? `HTTP ${c.httpStatus}` : '',
  ].filter(Boolean).join(' · ');
  const outcome = c.error
    ? `<p class="adm-warn">${esc(c.status)}: ${esc(c.error)}</p>`
    : c.inFlight ? '<p class="adm-hint">In flight — no reply yet. Refresh.</p>' : '';
  pane.innerHTML = `
    <p class="adm-status">${esc(meta)}</p>
    ${outcome}
    ${promptBlock('lv-c-ans', 'Answer', answerOf(c.response), true)}
    ${promptBlock('lv-c-user', 'User message — the posts', req.user)}
    ${req.rest ? promptBlock('lv-c-rest', 'Later turns — tool calls and results', req.rest) : ''}
    ${promptBlock('lv-c-sys', 'System prompt', req.system)}
    ${promptBlock('lv-c-raw', 'Raw response body', c.response ?? '')}`;
  wireCopy(pane);
}

interface FeedChannel {
  handle: string; enabled: boolean; note: string | null; reliability: string;
  modelGuidance: string | null;
  lastOkAt: string | null; lastError: string | null; lastErrorAt: string | null;
  postsSeen: number; stories: string[];
}

const RELIABILITIES = ['high', 'standard', 'low'] as const;

/** Channel coverage: the enabled set, each channel's health, reliability and
 *  story subscriptions. Health is the reason this is a table and not a
 *  textarea — a channel that 404s every poll used to look identical to a
 *  healthy one. Reliability is EDITORIAL: it annotates the feed's claim lines
 *  and deliberately feeds neither extraction nor routing. */
async function renderCoverage(host: HTMLElement): Promise<void> {
  let channels: FeedChannel[] = [];
  try {
    channels = (await get<{ channels: FeedChannel[] }>('/api/live/channels')).channels;
  } catch (err) {
    host.innerHTML = `<p class="adm-hint">Could not load channels: ${
      esc(err instanceof Error ? err.message : String(err))}</p>`;
    return;
  }

  const health = (c: FeedChannel) => {
    if (c.lastError) {
      return `<i class="adm-off" title="${esc(c.lastErrorAt ?? '')}">failing: ${
        esc(c.lastError.slice(0, 80))}</i>`;
    }
    if (c.lastOkAt) return `<span class="adm-on">ok ${relTime(c.lastOkAt)} ago</span>`;
    return '<i class="adm-hint">not polled yet</i>';
  };
  const relSelect = (c: FeedChannel) => `<select data-rel="${esc(c.handle)}"
      title="How much to trust this channel. Shown in the feed's claim line — a solo report from a low channel says so in words. Never fed to the extractor or the router.">
    ${RELIABILITIES.map((r) => `<option value="${r}"${c.reliability === r
      ? ' selected' : ''}>${r}</option>`).join('')}</select>`;
  // The treatment note: HOW to read this source, in the operator's words —
  // "unreliable but often breaks news first", "biased on strikes against X,
  // solid on Z", "overstates interception success". Reaches READERS as the
  // tooltip on every @channel citation (channel-meta); by the standing rule
  // it never reaches the extraction prompt and never routing.
  const noteInput = (c: FeedChannel) => `<input class="adm-note"
      data-note="${esc(c.handle)}" value="${esc(c.note ?? '')}"
      placeholder="how to treat — e.g. 'breaks news first, verify' / 'biased on X, solid on Z'"
      title="Shown to every reader as the tooltip on this channel's citations. Enter or click away to save." />`;
  // Model guidance (040): the treatment note's mirror image — written FOR the
  // extraction model, shown to no reader. It rides each of this channel's
  // posts into the prompt, where it may weigh confidence and the reading of
  // ambiguous wording but never the stated facts, a location, or whether an
  // event exists at all (SOURCE_NOTE_RULES is the contract).
  const guidanceInput = (c: FeedChannel) => `<input class="adm-note"
      data-mg="${esc(c.handle)}" value="${esc(c.modelGuidance ?? '')}"
      placeholder="tell the model — e.g. 'unreliable on first reports' / 'overstates interceptions'"
      title="Read by the extraction model with every post from this channel. It can lower or raise confidence and colour ambiguous wording — never change what a post stated, never supply a place, never drop an event. Not shown to readers. Enter or click away to save." />`;
  const rows = channels.map((c) => `
    <tr${c.enabled ? '' : ' class="adm-dim"'}>
      <td><b title="${esc(c.handle)}">${esc(channelLabel(c.handle) || c.handle)}</b></td>
      <td>${relSelect(c)}</td>
      <td>${noteInput(c)}</td>
      <td>${guidanceInput(c)}</td>
      <td>${c.stories.length
        ? c.stories.map((s) => `<code>${esc(s)}</code>`).join(' ')
        : '<i class="adm-hint">shared stream</i>'}</td>
      <td>${health(c)}</td>
      <td style="text-align:right">${c.postsSeen}</td>
      <td class="adm-row-actions">
        <button class="mini" data-toggle="${esc(c.handle)}"
          title="${c.enabled
            ? 'Stop polling. Keeps health history and story subscriptions.'
            : 'Resume polling this channel.'}">${
          c.enabled ? 'Disable' : 'Enable'}</button>
        <button class="mini" data-remove="${esc(c.handle)}"
          title="Remove entirely — forgets health history and story subscriptions. Events already ingested keep citing it.">✕</button>
      </td>
    </tr>`).join('');

  host.innerHTML = `
    ${channels.length ? `<div class="adm-scroll-x"><table class="adm-table">
      <thead><tr><th>Channel</th><th>Reliability</th>
        <th style="width:26%">Treatment</th>
        <th style="width:26%">Model guidance</th><th>Stories</th>
        <th>Health</th><th style="text-align:right">Posts</th><th></th></tr></thead>
      <tbody>${rows}</tbody></table></div>`
      : '<p class="adm-hint">No channels configured yet.</p>'}
    <div class="adm-actions-bar">
      <input id="lv-add" type="text"
        placeholder="@channel, t.me link, or an RSS/Atom feed URL"
        style="flex:1;min-width:0" />
      <button class="mini commit" id="lv-add-btn">Add channel</button>
    </div>`;

  const rerender = () => renderCoverage(host);

  const addBox = host.querySelector('#lv-add') as HTMLInputElement;
  const add = async () => {
    const handle = addBox.value.trim();
    if (!handle) return;
    try {
      await post('/api/live/channels', { handle });
      toast(`Added ${/^https?:\/\//i.test(handle) ? channelLabel(handle)
        : handle.startsWith('@') ? handle : `@${handle.replace(/^.*\//, '')}`}.`);
      await rerender();
    } catch (err) { fail(err); }
  };
  host.querySelector('#lv-add-btn')!.addEventListener('click', () => void add());
  addBox.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); void add(); }
  });

  host.querySelectorAll<HTMLSelectElement>('[data-rel]').forEach((sel) =>
    sel.addEventListener('change', async () => {
      try {
        await put(`/api/live/channels/${encodeURIComponent(sel.dataset.rel!)}`,
                  { reliability: sel.value });
        toast(`@${sel.dataset.rel} rated ${sel.value}.`);
      } catch (err) { fail(err); void rerender(); }
    }));

  host.querySelectorAll<HTMLInputElement>('[data-note]').forEach((box) => {
    const save = async () => {
      try {
        await put(`/api/live/channels/${encodeURIComponent(box.dataset.note!)}`,
                  { note: box.value.trim() });
        toast(`Treatment note saved for @${box.dataset.note}.`);
      } catch (err) { fail(err); void rerender(); }
    };
    box.addEventListener('change', () => void save());
    box.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); box.blur(); }
    });
  });

  host.querySelectorAll<HTMLInputElement>('[data-mg]').forEach((box) => {
    const save = async () => {
      try {
        await put(`/api/live/channels/${encodeURIComponent(box.dataset.mg!)}`,
                  { modelGuidance: box.value.trim() });
        toast(`Model guidance saved for @${box.dataset.mg} — the extractor `
          + 'picks it up within ~5 min.');
      } catch (err) { fail(err); void rerender(); }
    };
    box.addEventListener('change', () => void save());
    box.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); box.blur(); }
    });
  });

  host.querySelectorAll<HTMLButtonElement>('[data-toggle]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      const handle = btn.dataset.toggle!;
      const on = channels.find((c) => c.handle === handle)?.enabled ?? true;
      try {
        await put(`/api/live/channels/${encodeURIComponent(handle)}`,
                  { enabled: !on });
        await rerender();
      } catch (err) { fail(err); }
    }));

  host.querySelectorAll<HTMLButtonElement>('[data-remove]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      const handle = btn.dataset.remove!;
      if (!confirm(`Remove @${handle} entirely?\n\nThis forgets its health `
        + 'history and drops every story subscription to it. To just stop '
        + 'polling it, use Disable instead.')) return;
      try {
        await del(`/api/live/channels/${encodeURIComponent(handle)}`);
        toast(`Removed @${handle}.`);
        await rerender();
      } catch (err) { fail(err); }
    }));
}

// --- Unit types --------------------------------------------------------------

function renderTypes(host: HTMLElement, registry: RegistryDto): void {
  const catLabel = (key: string) =>
    registry.categories.find((c) => c.key === key)?.label ?? key;
  const placeCats = new Set(registry.categories
    .filter((c) => c.domain === 'place').map((c) => c.key));
  const rows = registry.types
    .slice().sort((a, b) => a.sort - b.sort)
    .map((t) => {
      const props = (t.attrSchema as { properties?: Record<string, { default?: unknown }> })
        .properties ?? {};
      const eff = props.range_km?.default;
      const radar = props.radar_range_km?.default;
      return `<tr>
        <td><input type="color" class="adm-tcolor" data-key="${esc(t.key)}"
          title="Marker colour" value="${esc(t.style.color ?? '#4f7fd9')}" /></td>
        <td>${esc(t.label)}</td><td><code>${esc(t.key)}</code></td>
        <td>${esc(catLabel(t.category))}</td>
        <td>${typeof eff === 'number' ? `${eff} km` : ''}</td>
        <td>${typeof radar === 'number' ? `${radar} km` : ''}</td>
        <td><select class="adm-ticon" data-key="${esc(t.key)}" title="Map glyph">${
          TYPE_ICON_NAMES.map((i) => `<option${
            i === (t.style.icon ?? 'dot') ? ' selected' : ''}>${i}</option>`).join('')
        }</select></td>
        <td><input type="number" class="adm-minzoom" data-key="${esc(t.key)}"
          min="0" max="15" step="1" title="Zoom this type's markers first draw at"
          value="${typeof t.style.minZoom === 'number' ? t.style.minZoom : ''}"
          placeholder="${placeCats.has(t.category) ? String(PLACE_MINZOOM) : '0'}" /></td>
      </tr>`;
    }).join('');
  host.innerHTML = `
    ${section('Unit types',
      'The taxonomy every object belongs to. Create new types from the '
      + 'Add-unit palette ("New type…"), where the heavy model drafts stats '
      + 'from Wikipedia and web search. Min zoom is where the map markers of '
      + 'a type start drawing. Colour and icon are editable here and take '
      + 'effect on the next reload — empty min zoom means the default (places '
      + String(PLACE_MINZOOM) + ', everything else always); watched places '
      + 'always draw.')}
    <table class="adm-table"><thead><tr>
      <th>Colour</th><th>Label</th><th>Key</th><th>Category</th>
      <th>Effective</th><th>Radar</th><th>Icon</th><th>Min zoom</th>
    </tr></thead><tbody>${rows}</tbody></table>`;
  // Colour and icon: same PATCH, same live registry patch-through. The map
  // image is BAKED per type (objects.drawChip registers entity-<key> once), so
  // an open map keeps the old chip until it reloads — said in the toast rather
  // than left as a surprise.
  const styleEdit = async (key: string, body: Record<string, string>,
                           apply: () => void) => {
    try {
      await patch(`/api/registry/types/${encodeURIComponent(key)}`, body);
      apply();
      toast('Saved — the map picks up the new marker on reload.');
    } catch (err) { fail(err); }
  };
  host.querySelectorAll<HTMLInputElement>('.adm-tcolor').forEach((inp) => {
    inp.addEventListener('change', () => {
      const t = registry.types.find((x) => x.key === inp.dataset.key);
      void styleEdit(inp.dataset.key!, { color: inp.value },
                     () => { if (t) t.style.color = inp.value; });
    });
  });
  host.querySelectorAll<HTMLSelectElement>('.adm-ticon').forEach((sel) => {
    sel.addEventListener('change', () => {
      const t = registry.types.find((x) => x.key === sel.dataset.key);
      void styleEdit(sel.dataset.key!, { icon: sel.value },
                     () => { if (t) t.style.icon = sel.value; });
    });
  });
  host.querySelectorAll<HTMLInputElement>('.adm-minzoom').forEach((inp) => {
    inp.addEventListener('change', async () => {
      const key = inp.dataset.key!;
      const raw = inp.value.trim();
      const minZoom = raw === '' ? null : Number(raw);
      if (minZoom !== null && (!Number.isFinite(minZoom) || minZoom < 0 || minZoom > 15)) {
        toast('Min zoom must be between 0 and 15.');
        return;
      }
      try {
        await patch(`/api/registry/types/${encodeURIComponent(key)}`, { minZoom });
        // Patch the shared registry object so the map's next filter rebuild
        // (any layer toggle) picks it up without a reload.
        const t = registry.types.find((x) => x.key === key);
        if (t) {
          if (minZoom === null) delete t.style.minZoom;
          else t.style.minZoom = minZoom;
        }
        toast('Saved — applies on the next layer toggle or reload.');
      } catch (err) { fail(err); }
    });
  });
}

// --- Streams -----------------------------------------------------------------

async function renderStreams(host: HTMLElement): Promise<void> {
  try {
    const { streams } = await get<{ streams: Record<string, unknown>[] }>('/api/streams');
    host.innerHTML = `
      ${section('Datastreams',
        'Time-series feeds (positions, sensor values) attached to objects.')}
      <table class="adm-table"><thead><tr>
        <th>Id</th><th>Type</th><th>Unit</th><th>Entity</th>
      </tr></thead><tbody>${streams.map((s) => `
        <tr><td><code>${esc(String(s.id ?? '')).slice(0, 8)}</code></td>
        <td>${esc(s.streamType ?? s.stream_type)}</td>
        <td>${esc(s.unit ?? '')}</td>
        <td><code>${esc(String(s.entityId ?? s.entity_id ?? '')).slice(0, 8)}</code></td></tr>`)
        .join('') || '<tr><td colspan="4" class="adm-hint">No streams yet.</td></tr>'}
      </tbody></table>`;
  } catch (err) {
    host.innerHTML = '<p class="adm-hint">Could not load streams.</p>';
    fail(err);
  }
}

// --- Map defaults ------------------------------------------------------------

async function renderMapDefaults(host: HTMLElement, registry: RegistryDto): Promise<void> {
  try {
    interface VolStyle { color: string; lineOpacity: number; fill: boolean;
      fillOpacity: number; lineWidth: number; }
    const [{ settings }, { stories }] = await Promise.all([
      get<{ settings: {
        defaultBasemap: string; defaultStory?: string;
        defaultLayers: Record<string, boolean>;
        terrainExaggeration?: number;
        eventFadeHours?: number; eventColorHours?: number;
        eventFreshMinutes?: number;
        volumeStyles?: Record<'radar' | 'engage', VolStyle>;
      } }>('/api/admin/settings'),
      // Unreachable stories cost the picker, not the whole tab.
      get<{ stories: StoryRow[] }>('/api/stories')
        .catch(() => ({ stories: [] as StoryRow[] })),
    ]);
    // Only a PUBLIC story can be the default (the server refuses the rest):
    // the choice rides /api/config to every signed-in client.
    const storyChoices = storyTree(stories.filter((s) => s.visibility === 'public'));
    const VOL_DEFAULTS: Record<'radar' | 'engage', VolStyle> = {
      radar: { color: '#38bdf8', lineOpacity: 0.42, fill: true, fillOpacity: 0.04, lineWidth: 1 },
      engage: { color: '#f4514e', lineOpacity: 0.5, fill: true, fillOpacity: 0.16, lineWidth: 1 },
    };
    const vol = (k: 'radar' | 'engage'): VolStyle =>
      ({ ...VOL_DEFAULTS[k], ...(settings.volumeStyles?.[k] ?? {}) });
    const volEditor = (k: 'radar' | 'engage', label: string): string => {
      const s = vol(k);
      return `<fieldset class="adm-vol" data-kind="${k}"><legend>${label}</legend>
        <label>Colour<input type="color" data-vs="color" value="${s.color}" /></label>
        <label>Line opacity<input type="number" data-vs="lineOpacity" min="0" max="1" step="0.02" value="${s.lineOpacity}" /></label>
        <label>Line width<input type="number" data-vs="lineWidth" min="0.5" max="4" step="0.5" value="${s.lineWidth}" /></label>
        <label class="adm-check"><input type="checkbox" data-vs="fill" ${s.fill ? 'checked' : ''} /> Fill</label>
        <label>Fill opacity<input type="number" data-vs="fillOpacity" min="0" max="0.6" step="0.01" value="${s.fillOpacity}" /></label>
      </fieldset>`;
    };
    host.innerHTML = `
      ${section('Map defaults', 'What new sessions see before they touch anything. Open a section to edit it.')}
      ${fold('Map defaults', 'Basemap, terrain and which layers start on.', `
      <div class="adm-card">
        <label>Default basemap<select id="md-basemap">${
          Object.entries(BASEMAP_LABELS).map(([k, label]) =>
            `<option value="${esc(k)}" ${k === settings.defaultBasemap ? 'selected' : ''}>
             ${esc(label)}</option>`).join('')}</select></label>
        <label>Default story<select id="md-story">
          <option value="">All stories</option>${storyChoices.map(({ story, depth }) =>
            `<option value="${esc(story.id)}" ${story.id === settings.defaultStory ? 'selected' : ''}>
             ${'&nbsp;&nbsp;'.repeat(depth)}${esc(story.name)}</option>`).join('')}</select></label>
        <p class="adm-hint">What the story picker opens on for anyone who has not
          chosen one yet — pick a group to open on several stories at once.
          Someone who has picked a story (or All stories) keeps their choice.</p>
        <label>Terrain exaggeration
          <input type="number" id="md-terrain" min="0" max="8" step="0.5"
            value="${settings.terrainExaggeration ?? 2}" /></label>
        <p class="adm-hint">Vertical scale for globe-mode 3D terrain (the ⛰ toggle).
          1 = true height, 2 = default, 0 = flat. Applies to new sessions.</p>
        <p class="adm-hint">Layers on by default:</p>
        <div class="adm-checks">${registry.categories.map((c) => `
          <label class="adm-check"><input type="checkbox" data-md="${esc(c.key)}"
            ${settings.defaultLayers[c.key] !== false ? 'checked' : ''} />
            ${esc(c.label)}</label>`).join('')}</div>
        <footer><button class="mini commit" id="md-save">Save</button></footer>
      </div>`, true)}

      ${fold('Live-event timing', 'How events age on the map and feed.', `
      <div class="adm-card">
        <label>Fade time (hours)
          <input type="number" id="md-fade" min="0" max="168" step="0.5"
            value="${settings.eventFadeHours ?? 12}" /></label>
        <p class="adm-hint">Hours until an event dims out and drops off the map/feed. 0 = never fade.</p>
        <label>Colour-change time (hours)
          <input type="number" id="md-color" min="0.1" max="72" step="0.5"
            value="${settings.eventColorHours ?? 3}" /></label>
        <p class="adm-hint">How long the pin colour ramps red → orange → grey.</p>
        <label>Flashing time (minutes)
          <input type="number" id="md-fresh" min="0" max="240" step="1"
            value="${settings.eventFreshMinutes ?? 10}" /></label>
        <p class="adm-hint">How long a new event announces itself — the ring and
          the size pulse in the Event markers tab both default to this, and
          either can override it per kind. Measured from the LAST report, so a
          corroborated event announces itself again.</p>
        <p class="adm-hint">The markers themselves — ring, halo, pulse, colour
          ramp and each kind's symbol — are the Event markers tab. The province
          pulse when an event lands is the Region flash tab.</p>
        <footer><button class="mini commit" id="mt-save">Save</button></footer>
      </div>`)}

      ${fold('Coverage volumes',
             'Default look of the 3D detection and engagement volumes.', `
      <div class="adm-card">
        <div class="adm-vols">${volEditor('radar', 'Detection (radar)')}${volEditor('engage', 'Engagement')}</div>
        <footer><button class="mini commit" id="mv-save">Save</button></footer>
      </div>`)}`;

    host.querySelector('#md-save')!.addEventListener('click', async () => {
      const layers: Record<string, boolean> = {};
      host.querySelectorAll<HTMLInputElement>('[data-md]').forEach((cb) => {
        layers[cb.dataset.md!] = cb.checked;
      });
      try {
        await put('/api/admin/settings', {
          defaultBasemap: (host.querySelector('#md-basemap') as HTMLSelectElement).value,
          defaultStory: (host.querySelector('#md-story') as HTMLSelectElement).value,
          terrainExaggeration: Number(
            (host.querySelector('#md-terrain') as HTMLInputElement).value),
          defaultLayers: layers,
        });
        toast('Map defaults saved.');
      } catch (err) { fail(err); }
    });
    host.querySelector('#mt-save')!.addEventListener('click', async () => {
      const num = (id: string) => Number((host.querySelector(id) as HTMLInputElement).value);
      try {
        await put('/api/admin/settings', {
          eventFadeHours: num('#md-fade'),
          eventColorHours: num('#md-color'),
          eventFreshMinutes: num('#md-fresh'),
        });
        toast('Event timing saved. New sessions pick it up on load.');
      } catch (err) { fail(err); }
    });
    host.querySelector('#mv-save')!.addEventListener('click', async () => {
      const styles: Record<string, VolStyle> = {};
      host.querySelectorAll<HTMLElement>('.adm-vol').forEach((fs) => {
        const kind = fs.dataset.kind!;
        const inp = (p: string) => fs.querySelector(`[data-vs="${p}"]`) as HTMLInputElement;
        styles[kind] = {
          color: inp('color').value,
          lineOpacity: Number(inp('lineOpacity').value),
          lineWidth: Number(inp('lineWidth').value),
          fill: inp('fill').checked,
          fillOpacity: Number(inp('fillOpacity').value),
        };
      });
      try {
        await put('/api/admin/settings', { volumeStyles: styles });
        toast('Coverage-volume defaults saved.');
      } catch (err) { fail(err); }
    });
  } catch (err) {
    host.innerHTML = '<p class="adm-hint">Could not load settings.</p>';
    fail(err);
  }
}

// --- event markers ------------------------------------------------------------
// How a new event announces itself on the map, per kind, plus the symbol each
// kind wears everywhere. One setting (`eventMarkerStyles`), one tab.

async function renderMarkers(host: HTMLElement): Promise<void> {
  try {
    const { settings } = await get<{ settings: { eventMarkerStyles?: unknown } }>(
      '/api/admin/settings');
    host.innerHTML = '<div id="mk-host"></div>';
    // The marker editor rebuilds itself whenever the CARD LIST changes (a kind
    // added or dropped, a colour stop added or removed) rather than splicing
    // rows around a running animation loop; `stop` tears the old loop down so
    // an orphaned canvas is never redrawn forever.
    const markerHost = host.querySelector('#mk-host') as HTMLElement;
    let stopMarkers: (() => void) | null = null;
    const paintMarkers = (cfg: EventMarkerConfig): void => {
      stopMarkers?.();
      markerHost.innerHTML = markerEditorHtml(cfg);
      stopMarkers = wireMarkerEditor(markerHost, cfg, async (next) => {
        try {
          await put('/api/admin/settings', { eventMarkerStyles: next });
          toast('Marker styles saved. Open tabs pick them up on reload.');
        } catch (err) { fail(err); }
      }, paintMarkers);
    };
    paintMarkers(normalizeConfig(settings.eventMarkerStyles));
  } catch (err) {
    host.innerHTML = '<p class="adm-hint">Could not load settings.</p>';
    fail(err);
  }
}

// --- region flash -------------------------------------------------------------
// The province/country pulse when a fresh event lands. One setting
// (`regionFlash`), one tab: which kinds trigger it, how many flashes, how
// long each runs, the freshness gate, and the intensity. Applied to THIS tab
// immediately via setRegionFlash; other tabs pick it up on reload, like every
// presentation setting.

async function renderRegionFlash(host: HTMLElement): Promise<void> {
  try {
    const { settings } = await get<{ settings: {
      regionFlash?: Partial<RegionFlashConfig>;
    } }>('/api/admin/settings');
    const cfg: RegionFlashConfig = { ...REGION_FLASH_DEFAULTS,
                                     ...(settings.regionFlash ?? {}) };
    const kinds = new Set(cfg.kinds);
    host.innerHTML = `
      ${section('Region flash',
                'When a fresh event lands on the map, its containing region '
                + 'flashes so it registers even off-screen. Applies to every '
                + 'reader; each user can still mute it with the Layers toggle.')}
      <div class="adm-card">
        <label class="adm-check"><input type="checkbox" id="rf-on"
          ${cfg.enabled ? 'checked' : ''} /> Enabled</label>
        <p class="adm-hint">Off = no region flashes at all, including rule-alert
          pings. Events themselves still land and pulse as markers.</p>
        <p class="adm-hint">Event kinds that flash their region:</p>
        <div class="adm-checks">${Object.entries(LIVE_KINDS).map(([k, m]) => `
          <label class="adm-check"><input type="checkbox" data-rf-kind="${esc(k)}"
            ${kinds.has(k) ? 'checked' : ''} /> ${m.glyph} ${esc(m.label)}</label>`).join('')}</div>
        <label>Region level<select id="rf-level">
          <option value="1" ${cfg.level === 1 ? 'selected' : ''}>Province</option>
          <option value="0" ${cfg.level === 0 ? 'selected' : ''}>Country</option>
        </select></label>
        <label>Flashes per event
          <input type="number" id="rf-pulses" min="1" max="6" step="1"
            value="${cfg.pulses}" /></label>
        <p class="adm-hint">How many times the area lights up when one event
          lands. 1 = a single soft pulse (the shipped default), 2–3 = a blink.</p>
        <label>Flash length (seconds)
          <input type="number" id="rf-seconds" min="0.5" max="15" step="0.5"
            value="${cfg.seconds}" /></label>
        <p class="adm-hint">Length of ONE flash — fade in, hold, fade out. Total
          time on screen is this × flashes.</p>
        <label>Only events younger than (minutes)
          <input type="number" id="rf-age" min="1" max="240" step="1"
            value="${cfg.maxAgeMinutes}" /></label>
        <p class="adm-hint">The freshness gate, by occurrence time — a catch-up
          batch after being away must not light up the whole theatre.</p>
        <label>Areas lit at once
          <input type="number" id="rf-max" min="1" max="8" step="1"
            value="${cfg.maxActive}" /></label>
        <label>Fill intensity
          <input type="number" id="rf-fill" min="0.02" max="0.6" step="0.02"
            value="${cfg.fillOpacity}" /></label>
        <label>Outline intensity
          <input type="number" id="rf-line" min="0.05" max="1" step="0.05"
            value="${cfg.lineOpacity}" /></label>
        <footer><button class="mini commit" id="rf-save">Save</button></footer>
      </div>`;
    host.querySelector('#rf-save')!.addEventListener('click', async () => {
      const num = (id: string) => Number((host.querySelector(id) as HTMLInputElement).value);
      const picked: string[] = [];
      host.querySelectorAll<HTMLInputElement>('[data-rf-kind]').forEach((cb) => {
        if (cb.checked) picked.push(cb.dataset.rfKind!);
      });
      const next = {
        enabled: (host.querySelector('#rf-on') as HTMLInputElement).checked,
        kinds: picked,
        level: Number((host.querySelector('#rf-level') as HTMLSelectElement).value),
        pulses: num('#rf-pulses'),
        seconds: num('#rf-seconds'),
        maxAgeMinutes: num('#rf-age'),
        maxActive: num('#rf-max'),
        fillOpacity: num('#rf-fill'),
        lineOpacity: num('#rf-line'),
      };
      try {
        const res = await put<{ settings: { regionFlash?: unknown } }>(
          '/api/admin/settings', { regionFlash: next });
        setRegionFlash(res.settings.regionFlash);   // this tab applies NOW
        toast('Region flash saved. Other open tabs pick it up on reload.');
      } catch (err) { fail(err); }
    });
  } catch (err) {
    host.innerHTML = '<p class="adm-hint">Could not load settings.</p>';
    fail(err);
  }
}

// --- aircraft (ADS-B) coverage ----------------------------------------------

interface AcQuery {
  id: string; kind: string; params: Record<string, unknown>;
  enabled: boolean; last_poll_at?: string | null;
  last_error?: string | null; last_count?: number; watchers?: number;
  stories?: { story: string; name: string; enabled: boolean }[];
}

interface AcWatch {
  id: string; story_id: string; story_name: string; query_id: string;
  kind: string; params: Record<string, unknown>; zone_buffer_km: number;
  enabled: boolean; note: string | null; has_zone: boolean;
  zone_source: 'explicit' | 'aoi+buffer' | 'none'; zone_km2: number | null;
}

interface AcTrigger {
  id: string; story_id: string | null; story_name: string | null; name: string;
  expr: string; cooldown_s: number; notify: boolean; enabled: boolean;
  last_fired_at: string | null; last_event_id: string | null;
  has_area: boolean; area_km2: number | null; inside: number;
}

// Type codes worth offering, and the list is the feature's premise made
// concrete: a conflict story and a wildfire story want disjoint sets, so the
// picker suggests both rather than pretending there is one "military
// aircraft" answer.
const AC_TYPE_PRESETS: [string, string][] = [
  ['K35R', 'KC-135R Stratotanker — aerial refuelling'],
  ['KC30', 'A330 MRTT — aerial refuelling'],
  ['RC13', 'RC-135 Rivet Joint — SIGINT'],
  ['P8', 'P-8 Poseidon — maritime patrol'],
  ['E3TF', 'E-3 Sentry — AWACS'],
  ['C17', 'C-17 Globemaster — strategic airlift'],
  ['C30J', 'C-130J Hercules — tactical airlift'],
  ['CL2T', 'CL-415 / DHC-515 — water bomber'],
  ['AT8T', 'Air Tractor AT-802F — single-engine air tanker'],
  ['S64F', 'Erickson S-64 Aircrane — heli-tanker'],
  ['H60', 'H-60 — utility / firefighting'],
];

function acWatching(kind: string, params: Record<string, unknown>): string {
  if (kind === 'mil') return 'all military';
  if (kind === 'type') return `type ${esc(String(params.type ?? '?'))}`;
  if (kind === 'squawk') return `squawk ${esc(String(params.squawk ?? '?'))}`;
  if (kind === 'point') {
    return `within ${esc(String(params.radius_nm ?? 250))} nm of `
      + `${esc(String(params.lat ?? '?'))}, ${esc(String(params.lon ?? '?'))}`;
  }
  return esc(kind);
}

async function renderAircraft(host: HTMLElement): Promise<void> {
  let watches: AcWatch[] = [];
  let queries: AcQuery[] = [];
  let stories: StoryRow[] = [];
  let trigs: AcTrigger[] = [];
  try {
    [watches, queries, stories, trigs] = await Promise.all([
      get<{ watches: AcWatch[] }>('/api/aircraft/watch').then((r) => r.watches),
      get<{ queries: AcQuery[] }>('/api/aircraft/queries').then((r) => r.queries),
      get<{ stories: StoryRow[] }>('/api/stories').then((r) => r.stories),
      get<{ triggers: AcTrigger[] }>('/api/aircraft/triggers').then((r) => r.triggers),
    ]);
  } catch (err) {
    host.innerHTML = `<p class="adm-hint">Could not load aircraft coverage: ${
      esc(err instanceof Error ? err.message : String(err))}</p>`;
    return;
  }

  // The zone readout names WHICH of the three states a watch is in rather than
  // showing a bare area, because the failure mode is invisible from a number:
  // an 'aoi+buffer' watch on a story with no AOI covers nothing at all, and
  // reads exactly like a working one until you notice the layer is empty.
  const zoneCell = (w: AcWatch) => {
    const k = Math.round((w.zone_km2 ?? 0) / 1000);
    if (w.zone_source === 'explicit') {
      return `<span class="adm-on">drawn</span> · ${k}k km²`;
    }
    if (w.zone_source === 'aoi+buffer') {
      return `story area + ${w.zone_buffer_km} km · ${k}k km²`;
    }
    return '<i class="adm-off">no area — this watch collects nothing</i>';
  };

  const health = (q: AcQuery | undefined) => {
    if (!q) return '<i class="adm-hint">—</i>';
    if (q.last_error) {
      return `<i class="adm-off">failing: ${esc(q.last_error.slice(0, 60))}</i>`;
    }
    if (q.last_poll_at) {
      return `<span class="adm-on">ok ${relTime(q.last_poll_at)} ago</span>`
        + ` · ${q.last_count ?? 0} seen`;
    }
    return '<i class="adm-hint">not polled yet</i>';
  };
  const byQuery = new Map(queries.map((q) => [q.id, q]));

  const rows = watches.map((w) => `
    <tr${w.enabled ? '' : ' class="adm-dim"'}>
      <td><b>${esc(w.story_name)}</b></td>
      <td>${acWatching(w.kind, w.params)}</td>
      <td>${zoneCell(w)}</td>
      <td>${health(byQuery.get(w.query_id))}</td>
      <td class="adm-row-actions">
        <button class="mini" data-draw="${esc(w.id)}"
          title="Draw an explicit zone on the map. Use this when the story&#39;s own area is the wrong ground — tankers supporting a Gulf strike orbit over the eastern Mediterranean, and widening the AOI to reach them would also change what the story FILES.">${
          w.has_zone ? 'Redraw zone' : 'Draw zone'}</button>
        ${w.has_zone ? `<button class="mini" data-clearzone="${esc(w.id)}"
          title="Drop the drawn zone and fall back to the story area plus buffer.">Use story area</button>` : ''}
        <button class="mini" data-toggle="${esc(w.id)}">${
          w.enabled ? 'Disable' : 'Enable'}</button>
        <button class="mini danger" data-del="${esc(w.id)}">Remove</button>
      </td>
    </tr>`).join('');

  const storyOpts = stories
    .filter((s) => s.status === 'active')
    .map((s) => `<option value="${esc(s.id)}">${esc(s.name)}</option>`).join('');

  // "occupied" and "armed" are different idle states and the row must say
  // which: an occupied trigger fires nothing more by design, and without the
  // word an operator reads the silence as broken.
  const trigState = (t: AcTrigger) => {
    if (!t.enabled) return '<i class="adm-off">disabled</i>';
    const fired = t.last_fired_at
      ? ` · fired ${relTime(t.last_fired_at)} ago` : '';
    if (t.inside > 0) {
      return `<span class="adm-on">occupied · ${t.inside} inside</span>${fired}`;
    }
    return `armed${fired}`;
  };
  const anythingPolled = queries.some((q) => q.enabled && (q.watchers ?? 0) > 0);

  const trigRows = trigs.map((t) => `
    <tr${t.enabled ? '' : ' class="adm-dim"'}>
      <td><b>${esc(t.name)}</b>${t.story_name
        ? `<br /><span class="adm-hint">${esc(t.story_name)}</span>` : ''}</td>
      <td>${t.expr ? `<code>${esc(t.expr)}</code>` : '<i>any aircraft</i>'}
        <br /><span class="adm-hint">cooldown ${Math.round(t.cooldown_s / 60)} min${
          t.notify ? ' · notifies' : ''}</span></td>
      <td>${t.has_area
        ? `<span class="adm-on">box</span> · ${Math.round((t.area_km2 ?? 0) / 1000)}k km²`
        : 'anywhere watched'}</td>
      <td>${trigState(t)}</td>
      <td class="adm-row-actions">
        <button class="mini" data-trig-draw="${esc(t.id)}">${
          t.has_area ? 'Redraw box' : 'Draw box'}</button>
        <button class="mini" data-trig-toggle="${esc(t.id)}">${
          t.enabled ? 'Disable' : 'Enable'}</button>
        <button class="mini danger" data-trig-del="${esc(t.id)}">Remove</button>
      </td>
    </tr>`).join('');

  host.innerHTML = `
    ${section('Aircraft coverage (ADS-B)',
      'What each story watches in the air, and over what ground. Coverage is '
      + 'per story on purpose: a Ukraine story wants military traffic over its '
      + 'own theatre, a Gulf story wants tankers and ISR over a zone reaching '
      + 'into the eastern Mediterranean, a wildfire story wants water bombers '
      + 'and no military aircraft at all. Each upstream query is polled once '
      + 'however many stories subscribe to it.')}
    <div class="adm-card wide">
      ${watches.length ? `<table class="adm-table">
        <thead><tr><th>Story</th><th>Watching</th><th>Zone</th>
          <th>Upstream</th><th></th></tr></thead>
        <tbody>${rows}</tbody></table>`
      : '<p class="adm-hint">No coverage yet — nothing is polled and the map '
        + 'layer stays empty. Add a watch below.</p>'}
    </div>

    ${section('Add coverage')}
    <div class="adm-card wide">
      <div class="adm-form-row">
        <label>Story <select id="ac-story">${storyOpts}</select></label>
        <label>Watch
          <select id="ac-kind">
            <option value="mil">All military aircraft</option>
            <option value="type">A specific type</option>
            <option value="squawk">A squawk code</option>
            <option value="point">Everything near a point</option>
          </select>
        </label>
      </div>
      <div class="adm-form-row" id="ac-p-type" hidden>
        <label>Type code
          <input id="ac-type" list="ac-types" placeholder="K35R"
            title="ICAO type designator. The upstream matches it exactly." />
        </label>
        <datalist id="ac-types">${AC_TYPE_PRESETS.map(([c, d]) =>
          `<option value="${c}">${esc(d)}</option>`).join('')}</datalist>
      </div>
      <div class="adm-form-row" id="ac-p-squawk" hidden>
        <label>Squawk
          <input id="ac-squawk" placeholder="7700"
            title="7700 emergency, 7600 radio failure, 7500 unlawful interference." />
        </label>
      </div>
      <div class="adm-form-row" id="ac-p-point" hidden>
        <label>Lat <input id="ac-lat" placeholder="32.0" style="width:7em" /></label>
        <label>Lon <input id="ac-lon" placeholder="52.0" style="width:7em" /></label>
        <label>Radius (nm)
          <input id="ac-radius" value="250" style="width:6em"
            title="Upstream maximum is 250 nm — anything larger is clamped, not refused." />
        </label>
      </div>
      <div class="adm-form-row">
        <label>Buffer around the story area (km)
          <input id="ac-buffer" value="300" style="width:6em"
            title="How far outside the story&#39;s own area to watch. Aircraft supporting an area orbit outside it, so 0 is almost never right. Ignored once an explicit zone is drawn." />
        </label>
        <button class="mini" id="ac-add">Add coverage</button>
      </div>
      <p class="adm-hint">Coverage over Iran, Iraq and the Gulf is thin — these
        are community receiver networks, nobody feeds from that airspace, and
        combat aircraft fly with transponders off. Expect tankers, transports
        and ISR rather than a live air picture. Europe, North America and most
        wildfire theatres are dense.</p>
    </div>

    ${section('Trigger areas',
      'An alarm, not a display: the first matching aircraft to enter the box '
      + 'while it is empty mints a live event — feed, map pulse, notification '
      + '— and aircraft joining an already-occupied box do not re-fire. The '
      + 'trigger re-arms once the box empties and the cooldown has passed.')}
    <div class="adm-card wide">
      ${trigs.length ? `<table class="adm-table">
        <thead><tr><th>Trigger</th><th>Fires for</th><th>Where</th>
          <th>State</th><th></th></tr></thead>
        <tbody>${trigRows}</tbody></table>`
      : '<p class="adm-hint">No triggers yet. Example: a box over the Gulf '
        + 'with condition <code>type = "K35R" or type = "KC30"</code> fires '
        + 'the moment refuellers take station — once, not per aircraft.</p>'}
      ${!anythingPolled ? '<p class="adm-hint">⚠ Nothing is being polled — a '
        + 'trigger only sees aircraft the coverage above collects. Its box '
        + 'joins the collection area automatically, but some watch must poll '
        + 'a query that returns the aircraft you care about.</p>' : ''}
      <div class="adm-form-row">
        <label>Name <input id="trig-name" placeholder="Gulf tanker box"
          style="width:12em" /></label>
        <label>Story
          <select id="trig-story"
            title="Optional. A trigger tied to a story pauses when the story goes dormant; with none it is always armed.">
            <option value="">— always armed —</option>${storyOpts}</select>
        </label>
        <label>Condition
          <input id="trig-expr" placeholder='type = "K35R" or type = "KC30"'
            style="width:20em"
            title='Rules DSL over the position: type (ICAO code), name (callsign), attrs.alt, attrs.gs, attrs.squawk, attrs.reg, attrs.desc, attrs.mil, attrs.hex. ~ means contains, e.g. attrs.desc ~ "tanker". Empty = any aircraft in the box.' />
        </label>
      </div>
      <div class="adm-form-row">
        <label>Cooldown (min)
          <input id="trig-cooldown" value="60" style="width:5em"
            title="Floor between fires. Absorbs the re-fire a coverage gap manufactures when a contact blinks out and back." />
        </label>
        <label><input type="checkbox" id="trig-notify" checked /> notification</label>
        <button class="mini" id="trig-box">Draw box</button>
        <button class="mini" id="trig-add">Add trigger</button>
      </div>
    </div>

    ${section('Upstream queries',
      'One poll per query however many stories subscribe. The upstream allows '
      + 'one request a second and offers no uptime guarantee, so this list — '
      + 'not the story count — is what that budget is spent on.')}
    <div class="adm-card wide">
      ${queries.length ? `<table class="adm-table">
        <thead><tr><th>Query</th><th>Subscribers</th><th>Health</th></tr></thead>
        <tbody>${queries.map((q) => `<tr${q.enabled ? '' : ' class="adm-dim"'}>
          <td>${acWatching(q.kind, q.params)}</td>
          <td>${(q.stories ?? []).length
            ? (q.stories ?? []).map((s) => `<code>${esc(s.name)}</code>`).join(' ')
            : '<i class="adm-hint">none — not polled</i>'}</td>
          <td>${health(q)}</td></tr>`).join('')}</tbody></table>`
      : '<p class="adm-hint">No queries yet.</p>'}
    </div>
    <div class="adm-actions-bar">
      <button class="mini" id="ac-poll">Poll now</button>
    </div>`;

  const el = <T extends HTMLElement>(sel: string) => host.querySelector(sel) as T;

  // Only the fields the chosen kind uses. A squawk box left visible on an "all
  // military" query is a field an operator can fill in and have silently
  // ignored, which is how a watch ends up not doing what its author meant.
  const kindSel = el<HTMLSelectElement>('#ac-kind');
  const syncKind = () => {
    for (const k of ['type', 'squawk', 'point']) {
      (el(`#ac-p-${k}`) as HTMLElement).hidden = kindSel.value !== k;
    }
  };
  kindSel.addEventListener('change', syncKind);
  syncKind();

  el('#ac-add').addEventListener('click', async () => {
    const kind = kindSel.value;
    const params: Record<string, unknown> = {};
    if (kind === 'type') params.type = el<HTMLInputElement>('#ac-type').value.trim();
    if (kind === 'squawk') params.squawk = el<HTMLInputElement>('#ac-squawk').value.trim();
    if (kind === 'point') {
      params.lat = Number(el<HTMLInputElement>('#ac-lat').value);
      params.lon = Number(el<HTMLInputElement>('#ac-lon').value);
      params.radius_nm = Number(el<HTMLInputElement>('#ac-radius').value) || 250;
    }
    try {
      await post('/api/aircraft/watch', {
        story: el<HTMLSelectElement>('#ac-story').value,
        kind,
        params,
        zone_buffer_km: Number(el<HTMLInputElement>('#ac-buffer').value) || 0,
      });
      toast('Coverage added — the next poll picks it up within a minute.');
      void renderAircraft(host);
    } catch (err) { fail(err); }
  });

  el('#ac-poll').addEventListener('click', async () => {
    try {
      const out = await post<{ queries: number; stored: number }>(
        '/api/aircraft/poll');
      toast(`Polled ${out.queries} quer${out.queries === 1 ? 'y' : 'ies'} — `
        + `${out.stored} position(s) stored.`);
      void renderAircraft(host);
    } catch (err) { fail(err); }
  });

  host.querySelectorAll<HTMLButtonElement>('[data-toggle]').forEach((b) => {
    b.addEventListener('click', async () => {
      const w = watches.find((x) => x.id === b.dataset.toggle);
      if (!w) return;
      try {
        await put(`/api/aircraft/watch/${w.id}`, { enabled: !w.enabled });
        void renderAircraft(host);
      } catch (err) { fail(err); }
    });
  });

  host.querySelectorAll<HTMLButtonElement>('[data-del]').forEach((b) => {
    b.addEventListener('click', async () => {
      if (!confirm('Remove this coverage?\n\nThe upstream query survives if '
        + 'another story still wants it, and stops being polled otherwise. '
        + 'Positions already recorded are kept.')) return;
      try {
        await del(`/api/aircraft/watch/${b.dataset.del}`);
        void renderAircraft(host);
      } catch (err) { fail(err); }
    });
  });

  host.querySelectorAll<HTMLButtonElement>('[data-clearzone]').forEach((b) => {
    b.addEventListener('click', async () => {
      try {
        await put(`/api/aircraft/watch/${b.dataset.clearzone}`, { zone: null });
        toast('Back to the story area plus buffer.');
        void renderAircraft(host);
      } catch (err) { fail(err); }
    });
  });

  host.querySelectorAll<HTMLButtonElement>('[data-draw]').forEach((b) => {
    b.addEventListener('click', async () => {
      const cap = adminHooks.captureArea;
      if (!cap) {
        toast('Drawing needs the cockpit map — reopen the console from there.');
        return;
      }
      const w = watches.find((x) => x.id === b.dataset.draw);
      if (!w) return;
      // Hidden, not closed — the same contract the story Area card keeps, so
      // every unsaved field on this page survives the draw.
      const overlay = document.querySelector('.admin') as HTMLElement | null;
      if (overlay) overlay.style.display = 'none';
      try {
        const ring = await cap(`Aircraft zone for ${w.story_name}`);
        if (ring) {
          await put(`/api/aircraft/watch/${w.id}`,
                    { zone: { type: 'Polygon', coordinates: [ring] } });
          toast('Zone saved — it replaces the story area for aircraft only.');
        }
      } catch (err) { fail(err); } finally {
        if (overlay) overlay.style.display = '';
        void renderAircraft(host);
      }
    });
  });

  // --- triggers ---------------------------------------------------------

  // The box drawn for a NEW trigger, held until Add posts it. The draw-then-
  // save order differs from the watch flow (draw after create) because the
  // box is the trigger's whole point — creating one without it would leave a
  // condition-only trigger armed over everything for a moment.
  let pendingBox: number[][] | null = null;

  el('#trig-box').addEventListener('click', async () => {
    const cap = adminHooks.captureArea;
    if (!cap) {
      toast('Drawing needs the cockpit map — reopen the console from there.');
      return;
    }
    const overlay = document.querySelector('.admin') as HTMLElement | null;
    if (overlay) overlay.style.display = 'none';
    try {
      const ring = await cap('Trigger box');
      if (ring) {
        pendingBox = ring;
        el('#trig-box').textContent = '✓ box drawn';
      }
    } catch (err) { fail(err); } finally {
      if (overlay) overlay.style.display = '';
    }
  });

  el('#trig-add').addEventListener('click', async () => {
    const name = el<HTMLInputElement>('#trig-name').value.trim();
    if (!name) { toast('Give the trigger a name.'); return; }
    const expr = el<HTMLInputElement>('#trig-expr').value.trim();
    if (!pendingBox && !expr) {
      toast('Draw a box or give a condition — with neither the trigger would '
        + 'fire for any aircraft anywhere.');
      return;
    }
    try {
      await post('/api/aircraft/triggers', {
        name,
        story: el<HTMLSelectElement>('#trig-story').value || null,
        expr,
        area: pendingBox
          ? { type: 'Polygon', coordinates: [pendingBox] } : null,
        cooldown_s: Math.max(0, Math.round(
          (Number(el<HTMLInputElement>('#trig-cooldown').value) || 0) * 60)),
        notify: el<HTMLInputElement>('#trig-notify').checked,
      });
      toast('Trigger armed — the next poll cycle evaluates it.');
      void renderAircraft(host);
    } catch (err) { fail(err); }
  });

  host.querySelectorAll<HTMLButtonElement>('[data-trig-toggle]').forEach((b) => {
    b.addEventListener('click', async () => {
      const t = trigs.find((x) => x.id === b.dataset.trigToggle);
      if (!t) return;
      try {
        await put(`/api/aircraft/triggers/${t.id}`, { enabled: !t.enabled });
        void renderAircraft(host);
      } catch (err) { fail(err); }
    });
  });

  host.querySelectorAll<HTMLButtonElement>('[data-trig-del]').forEach((b) => {
    b.addEventListener('click', async () => {
      if (!confirm('Remove this trigger?\n\nEvents it already minted stay — '
        + 'a trigger is configuration, not history.')) return;
      try {
        await del(`/api/aircraft/triggers/${b.dataset.trigDel}`);
        void renderAircraft(host);
      } catch (err) { fail(err); }
    });
  });

  host.querySelectorAll<HTMLButtonElement>('[data-trig-draw]').forEach((b) => {
    b.addEventListener('click', async () => {
      const cap = adminHooks.captureArea;
      if (!cap) {
        toast('Drawing needs the cockpit map — reopen the console from there.');
        return;
      }
      const t = trigs.find((x) => x.id === b.dataset.trigDraw);
      if (!t) return;
      const overlay = document.querySelector('.admin') as HTMLElement | null;
      if (overlay) overlay.style.display = 'none';
      try {
        const ring = await cap(`Trigger box for ${t.name}`);
        if (ring) {
          await put(`/api/aircraft/triggers/${t.id}`,
                    { area: { type: 'Polygon', coordinates: [ring] } });
          toast('Box saved.');
        }
      } catch (err) { fail(err); } finally {
        if (overlay) overlay.style.display = '';
        void renderAircraft(host);
      }
    });
  });
}
